import { type FormEvent, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  BriefcaseBusiness,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  ClipboardList,
  FileSpreadsheet,
  Home,
  Loader2,
  LogIn,
  LogOut,
  Plus,
  RefreshCw,
  ReceiptText,
  Search,
  Send,
  ShieldCheck,
  UploadCloud,
  UserRound,
  UsersRound,
} from "lucide-react";
import {
  apiFetch,
  canMaintainPayroll,
  clearAuth,
  getCurrentUser,
  login,
  setAuth,
  type CurrentUser,
} from "./lib/api";
import {
  loadFinanceWorkbenchSummary,
  type FinanceWorkbenchSummary,
} from "./lib/financeWorkbench";
import {
  currentMonth,
  draftFromBatch,
  draftIsReady,
  formatAmount,
  listNotifyLogs,
  listSalarySlips,
  loadDraftBatch,
  netAmountTotal,
  notifyLists,
  parseSalaryFile,
  saveDraftBatch,
  sendSalaryWecomNotifications,
  statusLabel,
  syncSalarySlips,
  type PayrollDraft,
  type SalaryNotifyLog,
  type SalarySlip,
} from "./lib/payroll";
import { CustomerCrmWorkbench } from "./CustomerCrmWorkbench";
import { CustomerReviewWorkbench } from "./CustomerReviewWorkbench";
import { SalesMobileHome } from "./SalesMobileHome";

type RouteState = {
  path: string;
  params: URLSearchParams;
};

type Toast = {
  tone: "success" | "warning" | "danger" | "info";
  message: string;
};

type EmployeeNotification = {
  id: string;
  type: string;
  title: string;
  content: string;
  relatedType?: string | null;
  relatedId?: string | null;
  sendChannel: "SYSTEM" | "WECOM" | "EMAIL";
  readAt?: string | null;
};

type EmployeeNotificationList = {
  items: EmployeeNotification[];
};

const APP_BASE_PATH = normalizeBasePath(import.meta.env.BASE_URL);
const IS_GRAY_RUNTIME = APP_BASE_PATH.startsWith("/employee-frontend-gray/");
function normalizeBasePath(value: string | undefined) {
  const trimmed = (value ?? "/").trim();
  if (!trimmed || trimmed === "/") {
    return "";
  }
  return `/${trimmed.replace(/^\/+|\/+$/g, "")}`;
}

function stripBasePath(pathname: string) {
  if (!APP_BASE_PATH) {
    return pathname;
  }
  if (pathname === APP_BASE_PATH) {
    return "/";
  }
  if (pathname.startsWith(`${APP_BASE_PATH}/`)) {
    return pathname.slice(APP_BASE_PATH.length) || "/";
  }
  return pathname;
}

function withBasePath(to: string) {
  if (!APP_BASE_PATH || /^[a-z][a-z0-9+.-]*:/i.test(to)) {
    return to;
  }
  return `${APP_BASE_PATH}${to.startsWith("/") ? to : `/${to}`}`;
}

function readRoute(): RouteState {
  return {
    path: stripBasePath(window.location.pathname),
    params: new URLSearchParams(window.location.search),
  };
}

function navigate(to: string) {
  window.history.pushState(null, "", withBasePath(to));
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function isWecomBrowser() {
  return /wxwork/i.test(window.navigator.userAgent);
}

function buildWecomLoginBridgeUrl(route: RouteState) {
  if (route.path === "/" || route.path === "/login") {
    return "/login";
  }

  const nextPath = `${route.path}${window.location.search}${window.location.hash}`;
  return `/login?next=${encodeURIComponent(nextPath)}`;
}

function userDisplayName(user: CurrentUser | null) {
  return user?.displayName ?? user?.name ?? user?.username ?? "未登录";
}

function normalizedText(...values: Array<string | null | undefined>) {
  return values.filter(Boolean).join(" ").trim().toLowerCase();
}

function hasAnyText(value: string, keywords: string[]) {
  return keywords.some((keyword) => value.includes(keyword.toLowerCase()));
}

function getDaochongEntryRole(user: CurrentUser | null) {
  if (!user) {
    return null;
  }

  const nameText = normalizedText(user.displayName, user.name, user.username, user.loginAccount, user.wecomUserId, user.wecomName);
  const departmentText = normalizedText(user.department);
  const roleText = normalizedText(user.roleCode, user.roleName, user.title);
  const permissionText = normalizedText(...(user.permissions ?? []));
  const allText = normalizedText(nameText, departmentText, roleText, permissionText);
  const isChengcheng = hasAnyText(nameText, ["程程", "chengcheng"]);
  const isFinance = hasAnyText(allText, ["周立猛", "立猛", "limeng", "财务", "財務", "finance", "daochong.finance"]);
  const isDaochong = hasAnyText(allText, ["道冲", "道沖", "daochong"]);
  const isTeacher = hasAnyText(roleText, ["老师", "老師", "主理人", "合作老师", "合作老師", "teacher", "therapist", "service"])
    || hasAnyText(nameText, ["燕子", "觉心", "覺心", "子青", "慧心", "慧欣", "huixin"]);
  const isUnconfirmedOfficeRole = hasAnyText(roleText, ["前台", "frontdesk", "front desk", "front_desk", "管理员", "管理員", "admin"]);

  if (isChengcheng) {
    return "chengcheng";
  }
  if (isFinance) {
    return "finance";
  }
  if (isDaochong && isTeacher && !isUnconfirmedOfficeRole) {
    return "teacher";
  }

  return null;
}

function isMobileWorkPath(path: string) {
  return path === "/mobile" || path.startsWith("/mobile/");
}

function isDaochongAutoEntryPath(path: string) {
  return path === "/mobile/work";
}

function buildDaochongEntryHref(role: string) {
  return `/mobile/work/daochong?role=${encodeURIComponent(role)}`;
}

function isFinanceOrPayrollWorkspacePath(path: string) {
  return path === "/"
    || path === "/finance/imports"
    || path === "/finance/workbench"
    || path === "/finance/workbench-gray"
    || path === "/payroll/batch"
    || path === "/payroll/mine";
}

function getAuthenticatedDefaultPath(user: CurrentUser) {
  return canMaintainPayroll(user) ? `/payroll/batch?month=${currentMonth()}` : "/mobile";
}

function getAuthenticatedRouteRedirect(user: CurrentUser, path: string) {
  if (path === "/login") {
    return getAuthenticatedDefaultPath(user);
  }

  if (!canMaintainPayroll(user) && isFinanceOrPayrollWorkspacePath(path)) {
    return "/mobile";
  }

  return null;
}

function getPostLoginPath(user: CurrentUser, path: string, search: string) {
  return getAuthenticatedRouteRedirect(user, path) ?? `${path}${search}`;
}

function formatMobileTodayLabel(date = new Date()) {
  const weekDays = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${month}/${day} ${weekDays[date.getDay()]}`;
}

function buildMobileWorkPreviewUser(route: RouteState): CurrentUser | null {
  if (!import.meta.env.DEV || !isMobileWorkPath(route.path)) {
    return null;
  }

  const preview = route.params.get("preview");
  if (!preview) {
    return null;
  }

  const baseUser = {
    id: `preview-${preview}`,
    username: preview,
    loginAccount: preview,
    permissions: [],
    roleCode: "EMPLOYEE",
    wecomUserId: null,
    wecomName: null,
  };
  if (preview === "founder" || preview === "edwardtsuei") {
    return {
      ...baseUser,
      displayName: "崔以达",
      name: "崔以达",
      department: "管理中心",
      roleCode: "SUPER_ADMIN",
      roleName: "创办人",
      title: "创办人",
    };
  }
  if (preview === "sales" || preview === "sun") {
    return {
      ...baseUser,
      displayName: "孙小松",
      name: "孙小松",
      department: "线下渠道销售部",
      permissions: [
        "page.customers.list",
        "page.customers.detail",
        "action.customer.create",
        "action.customer.update",
        "action.schedule.create",
      ],
      roleCode: "SALES_MANAGER",
      roleName: "线下渠道销售总监",
      title: "线下渠道销售总监",
    };
  }
  if (preview === "chengcheng") {
    return {
      ...baseUser,
      displayName: "程程",
      name: "程程",
      department: "道冲元气",
      roleName: "主理人",
      title: "主理人",
    };
  }
  if (preview === "finance" || preview === "limeng") {
    return {
      ...baseUser,
      displayName: preview === "limeng" ? "立猛" : "财务",
      name: preview === "limeng" ? "立猛" : "财务",
      department: "财务",
      roleCode: "FINANCE",
      roleName: "财务",
      title: "财务",
    };
  }
  if (preview === "teacher" || preview === "daochong-teacher" || preview === "huixin") {
    return {
      ...baseUser,
      displayName: preview === "huixin" ? "慧心" : "燕子",
      name: preview === "huixin" ? "慧心" : "燕子",
      department: "道冲元气",
      roleName: "老师",
      title: "老师",
    };
  }
  if (preview === "yinxiaojuan" || preview === "ecommerce") {
    return {
      ...baseUser,
      displayName: "尹筱娟",
      name: "尹筱娟",
      department: "电商部",
      roleCode: "ECOMMERCE_MANAGER",
      roleName: "电商负责人",
      title: "运营",
    };
  }
  if (preview === "tanxi" || preview === "ecotech") {
    return {
      ...baseUser,
      displayName: "譚喜",
      name: "譚喜",
      department: "洄歸生態科技",
      roleCode: "ECOTECH_MANAGER",
      roleName: "洄归生态科技",
      title: "市場經理",
      wecomUserId: "TanXi",
      wecomName: "谭喜",
    };
  }
  if (preview === "greatchef" || preview === "bearhug") {
    return {
      ...baseUser,
      displayName: "申琦",
      name: "申琦",
      department: "熊抱大地",
      roleCode: "BEARHUG_KITCHEN",
      roleName: "熊抱后厨",
      title: "主厨 / 后厨负责人",
    };
  }
  if (preview === "plain") {
    return {
      ...baseUser,
      displayName: "普通员工",
      name: "普通员工",
      department: "办公室",
      roleName: "员工",
      title: "员工",
    };
  }

  return null;
}

type DaochongWorkRole = "teacher" | "principal";
type DaochongWorkTab = "agenda" | "schedule" | "work" | "mine";
type DaochongAgendaScope = "today" | "week";
type DaochongActivityScope = "today" | "week" | "month";
type DaochongRosterBusinessScope = "all" | "bearhug" | "daochong";
type DaochongTone = "forest" | "amber" | "blue" | "neutral";

type DaochongListItem = {
  title: string;
  detail: string;
  badge: string;
  time?: string;
  tone: DaochongTone;
};

type DaochongEntryItem = {
  title: string;
  detail: string;
  href?: string;
  group?: "常用" | "服务" | "财务";
  principalOnly?: boolean;
};

type DaochongMetricItem = {
  label: string;
  value: string;
};

type DaochongWorkbenchSummary = {
  generatedAt: string;
  role: DaochongWorkRole;
  permissions: {
    canViewTeamSchedule: boolean;
    canViewSchedule: boolean;
    canViewWeeklyReports: boolean;
  };
  metrics: {
    agenda: DaochongMetricItem[];
    schedule: DaochongMetricItem[];
  };
  appointments: Record<DaochongAgendaScope, DaochongListItem[]>;
  todos: DaochongListItem[];
  scheduleItems: Record<DaochongAgendaScope, DaochongListItem[]>;
  businessRosters: Record<DaochongAgendaScope, { bearhug: DaochongListItem[]; daochong: DaochongListItem[] }>;
  rosterSummaries: Record<DaochongAgendaScope, { title: string; detail: string }>;
  rosterNotes: Record<DaochongAgendaScope, DaochongListItem[]>;
  rosterSupport: Record<DaochongAgendaScope, DaochongListItem[]>;
  activities: Record<DaochongActivityScope, { label: string; front: DaochongListItem[]; kitchen: DaochongListItem[] }>;
  diagnostics: Array<{
    label: string;
    status: "success" | "blocked" | "warning";
    note: string;
  }>;
};

type MobileExpenseFormState = {
  title: string;
  category: string;
  amount: string;
  summary: string;
  payee: string;
  accountHint: string;
};

type ExpenseClaimMutationResponse = {
  workspace?: {
    expenseClaims?: Array<{
      id: string;
      title: string;
      amount: number;
      status: string;
      submittedAt: string;
    }>;
  };
};

const SCHEDULE_HREF = "/schedule?from=daochong-mobile";
const FULL_SHIFT_ROSTER_HREF = "/schedule/shifts?from=daochong-mobile";
const SHIFT_ROSTER_HREF = "/mobile/work/daochong?tab=schedule&from=daochong-mobile";
const WEEKLY_REPORTS_HREF = "/work-management/weekly-reports?from=daochong-mobile";
const ECOM_WEEKLY_HREF = "/work-management/ecom-weekly?from=mobile-ecommerce";
const CREATE_WEEKLY_HREF = "/work-management/weekly-reports?from=daochong-plus&intent=create-weekly";
const MEETING_MINUTES_HREF = "/work-management/meeting-minutes?from=daochong-mobile";
const PROJECT_DISCUSSION_HREF = "/work-management/weekly-reports?from=daochong-discussion#discussion";
const FOUNDER_FINANCE_DASHBOARD_HREF = "/finance/dashboard?from=founder-home";
const FOUNDER_WEEKLY_REPORTS_HREF = "/work-management/weekly-reports?archive=1&archiveView=team&from=founder-home";
const FOUNDER_SHIFT_ROSTER_HREF = "/schedule/shifts?from=founder-home";
const FOUNDER_MEETING_MINUTES_HREF = "/work-management/meeting-minutes?scope=team&from=founder-home";
const FOUNDER_ECOTECH_CUSTOMERS_HREF = "/ecotech/customers?from=founder-home";
const FOUNDER_MEMBERS_HREF = "/management/members?from=founder-home";
const FOUNDER_COURSE_PROJECTS_HREF = "/courses/projects?from=founder-home";
const EXPENSE_HREF = "/mobile/expense";

const daochongRoleMeta: Record<DaochongWorkRole, { label: string; lead: string }> = {
  teacher: {
    label: "老师",
    lead: "先看全店，再处理自己的待办。",
  },
  principal: {
    label: "主理人",
    lead: "先看全店，再处理重点。",
  },
};

const daochongAgendaLabels: Record<DaochongAgendaScope, string> = {
  today: "今日",
  week: "本周",
};

const daochongAppointments: Record<DaochongAgendaScope, DaochongListItem[]> = {
  today: [
    { time: "同步", title: "预约信息待接入", detail: "暂不显示示例预约；请进入日程系统查看真实到店、改约和服务状态。", badge: "真实源", tone: "forest" },
  ],
  week: [
    { time: "同步", title: "本周预约待接入", detail: "暂不显示示例周预约；真实排期以大爱归心日程系统为准。", badge: "真实源", tone: "forest" },
  ],
};

const daochongTodos: Record<DaochongWorkRole, DaochongListItem[]> = {
  teacher: [
    { title: "个人待办待接入", detail: "暂不显示示例客户和结算提醒；周报、会议纪要和报销入口已可进入真实系统。", badge: "待接入", tone: "neutral" },
  ],
  principal: [
    { title: "团队待办待接入", detail: "暂不显示示例审批数量；周报、会议纪要和报销入口已可进入真实系统。", badge: "待接入", tone: "neutral" },
  ],
};

const daochongScheduleItems: Record<DaochongAgendaScope, DaochongListItem[]> = {
  today: [
    { title: "今日班表待接入", detail: "暂不显示示例在岗老师；请进入排班系统查看真实班次、备注和支援安排。", badge: "真实源", tone: "forest" },
  ],
  week: [
    { title: "本周班表待接入", detail: "暂不显示示例周班表；请进入排班系统查看真实周视角。", badge: "真实源", tone: "forest" },
  ],
};

const daochongBusinessRosters: Record<DaochongAgendaScope, { bearhug: DaochongListItem[]; daochong: DaochongListItem[] }> = {
  today: {
    bearhug: [
      { title: "今日暂无熊抱班次", detail: "已读取共享班表，当前没有熊抱前厅或后厨在岗班次。", badge: "熊抱", tone: "blue" },
    ],
    daochong: daochongScheduleItems.today,
  },
  week: {
    bearhug: [
      { title: "本周暂无熊抱班次", detail: "已读取共享班表，当前范围没有熊抱前厅或后厨在岗班次。", badge: "熊抱", tone: "blue" },
    ],
    daochong: daochongScheduleItems.week,
  },
};

const daochongRosterSummaries: Record<DaochongAgendaScope, { title: string; detail: string }> = {
  today: {
    title: "班表数据待接入",
    detail: "暂不显示示例在岗人数；完整班次、备注和支援安排请进入排班系统查看。",
  },
  week: {
    title: "本周班表待接入",
    detail: "暂不显示示例周班表；真实班次以排班系统为准。",
  },
};

const daochongRosterNotes: Record<DaochongAgendaScope, DaochongListItem[]> = {
  today: [
    { title: "班表备注待接入", detail: "暂不显示示例备注；真实备注请进入排班系统查看。", badge: "待接入", tone: "neutral" },
  ],
  week: [
    { title: "本周备注待接入", detail: "暂不显示示例周备注；真实备注请进入排班系统查看。", badge: "待接入", tone: "neutral" },
  ],
};

const daochongRosterSupport: Record<DaochongAgendaScope, DaochongListItem[]> = {
  today: [
    { title: "支援安排待接入", detail: "暂不显示示例支援提醒；真实支援请进入排班系统查看。", badge: "待接入", tone: "neutral" },
  ],
  week: [
    { title: "本周支援待接入", detail: "暂不显示示例活动支援；真实支援请进入排班系统查看。", badge: "待接入", tone: "neutral" },
  ],
};

const daochongActivities: Record<DaochongActivityScope, { label: string; front: DaochongListItem[]; kitchen: DaochongListItem[] }> = {
  today: {
    label: "今日",
    front: [
      { title: "熊抱前厅信息待接入", detail: "暂不显示示例活动；真实前厅事项以活动系统为准。", badge: "待接入", tone: "neutral" },
    ],
    kitchen: [
      { title: "熊抱后厨信息待接入", detail: "暂不显示示例活动；真实后厨事项以活动系统为准。", badge: "待接入", tone: "neutral" },
    ],
  },
  week: {
    label: "本周",
    front: [
      { title: "本周前厅信息待接入", detail: "暂不显示示例周活动；真实前厅事项以活动系统为准。", badge: "待接入", tone: "neutral" },
    ],
    kitchen: [
      { title: "本周后厨信息待接入", detail: "暂不显示示例周活动；真实后厨事项以活动系统为准。", badge: "待接入", tone: "neutral" },
    ],
  },
  month: {
    label: "本月",
    front: [
      { title: "本月前厅信息待接入", detail: "暂不显示示例月活动；真实前厅事项以活动系统为准。", badge: "待接入", tone: "neutral" },
    ],
    kitchen: [
      { title: "本月后厨信息待接入", detail: "暂不显示示例月活动；真实后厨事项以活动系统为准。", badge: "待接入", tone: "neutral" },
    ],
  },
};

const daochongWorkEntries: DaochongEntryItem[] = [
  { title: "预约信息", detail: "到店、改约、服务状态" },
  { title: "客户充值", detail: "截图、现金照片、审批" },
  { title: "服务结算", detail: "耗卡、优惠、推荐人" },
  { title: "服务纪要", detail: "12 小时提醒和归档" },
  { title: "客户档案", detail: "服务记录、偏好和回访" },
  { title: "我的业绩", detail: "目标、提成和本人可见数据" },
  { title: "项目讨论", detail: "进入周报讨论区记录协作", href: PROJECT_DISCUSSION_HREF },
  { title: "会议纪要", detail: "结论、待办和负责人", href: `${MEETING_MINUTES_HREF}&entry=work` },
  { title: "报销申请", detail: "物料、活动支出和附件", href: EXPENSE_HREF },
  { title: "团队奖金", detail: "程程填写原因和金额", principalOnly: true },
  { title: "财务汇总", detail: "耗卡、奖金、报销草稿", principalOnly: true },
  { title: "成员权限", detail: "由程程维护老师权限", principalOnly: true },
];

const daochongCreateEntries: DaochongEntryItem[] = [
  { title: "创建周报", detail: "本周总结和下周计划", href: CREATE_WEEKLY_HREF, group: "常用" },
  { title: "项目讨论", detail: "进入周报讨论区记录协作", href: PROJECT_DISCUSSION_HREF, group: "常用" },
  { title: "会议纪要", detail: "结论、待办和归档", href: `${MEETING_MINUTES_HREF}&entry=plus`, group: "常用" },
  { title: "报销申请", detail: "差旅、物料、活动支出", href: EXPENSE_HREF, group: "常用" },
  { title: "添加预约", detail: "新客、复诊、临时加号", group: "服务" },
  { title: "服务结算", detail: "完成服务后补凭证", group: "服务" },
  { title: "客户充值", detail: "截图、现金照片、程程审批", group: "财务" },
  { title: "团队奖金", detail: "程程填写原因和金额", group: "财务", principalOnly: true },
];

function getDaochongWorkRole(user: CurrentUser): DaochongWorkRole {
  return getDaochongEntryRole(user) === "chengcheng" ? "principal" : "teacher";
}

function getDaochongInitialTab(route?: RouteState): DaochongWorkTab {
  const tab = route?.params.get("tab");
  return tab === "schedule" || tab === "work" || tab === "mine" ? tab : "agenda";
}

function DaochongBadge({ children, tone }: { children: React.ReactNode; tone: DaochongTone }) {
  return <span className={`daochong-badge ${tone}`}>{children}</span>;
}

function DaochongList({ items }: { items: DaochongListItem[] }) {
  return (
    <div className="daochong-list">
      {items.map((item) => (
        <article className="daochong-list-item" key={`${item.title}-${item.badge}`}>
          <div>
            <strong>{item.title}</strong>
            <span>{item.detail}</span>
          </div>
          <DaochongBadge tone={item.tone}>{item.badge}</DaochongBadge>
        </article>
      ))}
    </div>
  );
}

function DaochongTimelineList({ items }: { items: DaochongListItem[] }) {
  return (
    <div className="daochong-timeline-list">
      {items.map((item) => (
        <article className="daochong-timeline-item" key={`${item.time ?? item.title}-${item.badge}`}>
          <span className="daochong-timeline-time">{item.time ?? "待办"}</span>
          <div>
            <strong>{item.title}</strong>
            <span>{item.detail}</span>
          </div>
          <DaochongBadge tone={item.tone}>{item.badge}</DaochongBadge>
        </article>
      ))}
    </div>
  );
}

function DaochongRosterRows({ items }: { items: DaochongListItem[] }) {
  return (
    <div className="daochong-roster-rows">
      {items.map((item) => (
        <article className={`daochong-roster-row ${item.tone}`} key={`${item.title}-${item.badge}`}>
          <span>{item.time ?? item.badge}</span>
          <div>
            <strong>{item.title}</strong>
            <p>{item.detail}</p>
          </div>
        </article>
      ))}
    </div>
  );
}

function DaochongRosterSignal({ title, items }: { title: string; items: DaochongListItem[] }) {
  const firstItem = items[0];
  return (
    <article className="daochong-roster-signal">
      <span>{title}</span>
      <strong>{firstItem?.title ?? "暂无信息"}</strong>
      <p>{firstItem?.detail ?? "已读取共享班表，当前范围没有填写事项。"}</p>
    </article>
  );
}

function DaochongRosterMap({
  activity,
  appointments,
  businessRosters,
  businessScope,
  onBusinessScopeChange,
  rosterNotes,
  rosterSupport,
  scope,
}: {
  activity: { label: string; front: DaochongListItem[]; kitchen: DaochongListItem[] };
  appointments: DaochongListItem[];
  businessRosters: { bearhug: DaochongListItem[]; daochong: DaochongListItem[] };
  businessScope: DaochongRosterBusinessScope;
  onBusinessScopeChange: (scope: DaochongRosterBusinessScope) => void;
  rosterNotes: DaochongListItem[];
  rosterSupport: DaochongListItem[];
  scope: DaochongAgendaScope;
}) {
  const showBearhug = businessScope === "all" || businessScope === "bearhug";
  const showDaochong = businessScope === "all" || businessScope === "daochong";
  const headline = scope === "today" ? "今天谁在现场，一屏看清" : "本周两店班次，一屏对齐";

  return (
    <section className="daochong-roster-map" aria-label="两店班表">
      <div className="daochong-roster-lead">
        <div>
          <span>两店班表</span>
          <strong>{headline}</strong>
          <p>前厅后厨、疗愈服务和活动备注集中查看。</p>
        </div>
        <a className="daochong-roster-manage" href={FULL_SHIFT_ROSTER_HREF}>完整管理</a>
      </div>

      <div className="daochong-roster-filter" role="tablist" aria-label="业务范围">
        {([
          ["all", "全店"],
          ["bearhug", "熊抱"],
          ["daochong", "道冲"],
        ] as Array<[DaochongRosterBusinessScope, string]>).map(([value, label]) => (
          <button
            aria-selected={businessScope === value}
            className={businessScope === value ? "active" : ""}
            key={value}
            onClick={() => onBusinessScopeChange(value)}
            role="tab"
            type="button"
          >
            {label}
          </button>
        ))}
      </div>

      <div className="daochong-roster-business-board">
        {showBearhug ? (
          <article className="daochong-roster-business bearhug">
            <div className="daochong-roster-business-title">
              <span>熊抱大地</span>
              <strong>前厅 · 后厨</strong>
            </div>
            <DaochongRosterRows items={businessRosters.bearhug} />
          </article>
        ) : null}

        {showDaochong ? (
          <article className="daochong-roster-business daochong">
            <div className="daochong-roster-business-title">
              <span>道冲元气</span>
              <strong>疗愈服务</strong>
            </div>
            <DaochongRosterRows items={businessRosters.daochong} />
          </article>
        ) : null}
      </div>

      <div className="daochong-roster-signals">
        <DaochongRosterSignal title="熊抱事项" items={[activity.front[0], activity.kitchen[0]].filter((item): item is DaochongListItem => Boolean(item))} />
        <DaochongRosterSignal title="道冲备注" items={rosterNotes} />
        <DaochongRosterSignal title="支援安排" items={rosterSupport} />
        <DaochongRosterSignal title="预约参考" items={appointments} />
      </div>
    </section>
  );
}

function DaochongPanel({
  action,
  children,
  title,
}: {
  action?: React.ReactNode;
  children: React.ReactNode;
  title: string;
}) {
  return (
    <section className="daochong-panel">
      <div className="daochong-panel-title">
        <strong>{title}</strong>
        {action}
      </div>
      {children}
    </section>
  );
}

function DaochongEntryGrid({ items, onFallback }: { items: DaochongEntryItem[]; onFallback: () => void }) {
  return (
    <div className="daochong-entry-grid">
      {items.map((item) =>
        item.href ? (
          <a className="daochong-entry-card" href={item.href} key={item.title}>
            <strong>{item.title}</strong>
            <span>{item.detail}</span>
          </a>
        ) : (
          <button className="daochong-entry-card" key={item.title} onClick={onFallback} type="button">
            <strong>{item.title}</strong>
            <span>{item.detail}</span>
          </button>
        ),
      )}
    </div>
  );
}

function DaochongGroupedEntryGrid({ items, onFallback }: { items: DaochongEntryItem[]; onFallback: () => void }) {
  const groups: Array<NonNullable<DaochongEntryItem["group"]>> = ["常用", "服务", "财务"];

  return (
    <div className="daochong-create-groups">
      {groups.map((group) => {
        const groupItems = items.filter((item) => (item.group ?? "常用") === group);
        if (!groupItems.length) {
          return null;
        }
        return (
          <section className="daochong-create-group" key={group}>
            <span>{group}</span>
            <DaochongEntryGrid items={groupItems} onFallback={onFallback} />
          </section>
        );
      })}
    </div>
  );
}

function DaochongWorkPage({ route, user }: { route?: RouteState; user: CurrentUser }) {
  const routeSearch = route?.params.toString() ?? "";
  const [activeTab, setActiveTab] = useState<DaochongWorkTab>(() => getDaochongInitialTab(route));
  const [agendaScope, setAgendaScope] = useState<DaochongAgendaScope>("today");
  const [activityScope, setActivityScope] = useState<DaochongActivityScope>("today");
  const [rosterBusinessScope, setRosterBusinessScope] = useState<DaochongRosterBusinessScope>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [summary, setSummary] = useState<DaochongWorkbenchSummary | null>(null);
  const [summaryStatus, setSummaryStatus] = useState<"loading" | "ready" | "error">("loading");
  const [summaryError, setSummaryError] = useState("");
  const role = getDaochongWorkRole(user);
  const roleMeta = daochongRoleMeta[role];
  const visibleWorkEntries = daochongWorkEntries.filter((item) => !item.principalOnly || role === "principal");
  const visibleCreateEntries = daochongCreateEntries.filter((item) => !item.principalOnly || role === "principal");
  const agendaLabel = daochongAgendaLabels[agendaScope];
  const summaryAppointments = summary?.appointments ?? daochongAppointments;
  const summaryTodos = summary?.todos?.length ? summary.todos : daochongTodos[role];
  const summaryBusinessRosters = summary?.businessRosters ?? daochongBusinessRosters;
  const summaryRosterSummaries = summary?.rosterSummaries ?? daochongRosterSummaries;
  const summaryRosterNotes = summary?.rosterNotes ?? daochongRosterNotes;
  const summaryRosterSupport = summary?.rosterSupport ?? daochongRosterSupport;
  const summaryActivities = summary?.activities ?? daochongActivities;
  const activity = summaryActivities[activityScope];
  const displayName = userDisplayName(user);
  const heroMeta = activeTab === "schedule"
      ? {
        eyebrow: "排班",
        title: "当天运营地图",
        copy: "看熊抱大地和道冲元气的班次、备注和支援。",
      }
    : activeTab === "work"
      ? {
          eyebrow: role === "principal" ? "管理" : "工作",
          title: role === "principal" ? "管理事项" : "工作入口",
          copy: "处理项目、纪要、报销和服务记录。",
        }
      : activeTab === "mine"
        ? {
            eyebrow: "我的",
            title: "个人事项",
            copy: "查看周报、待办和账号身份。",
          }
        : {
            eyebrow: "日程",
            title: "日程安排",
            copy: roleMeta.lead,
          };
  const stats = activeTab === "schedule"
    ? summary?.metrics.schedule ?? [
        { label: "道冲", value: "待接" },
        { label: "熊抱", value: "待接" },
        { label: "备注", value: "待接" },
        { label: "视角", value: agendaLabel },
      ]
    : summary?.metrics.agenda ?? [
        { label: "预约", value: "待接" },
        { label: "待办", value: "待接" },
        { label: "活动", value: "待接" },
        { label: "在岗", value: "待接" },
      ];

  useEffect(() => {
    let alive = true;
    setSummaryStatus("loading");
    setSummaryError("");
    apiFetch<DaochongWorkbenchSummary>("/daochong/workbench-summary")
      .then((data) => {
        if (!alive) return;
        setSummary(data);
        setSummaryStatus("ready");
      })
      .catch((error) => {
        if (!alive) return;
        setSummary(null);
        setSummaryStatus("error");
        setSummaryError(error instanceof Error ? error.message : "读取失败");
      });
    return () => {
      alive = false;
    };
  }, [user.id]);

  useEffect(() => {
    setActiveTab(getDaochongInitialTab(route));
  }, [route?.path, routeSearch]);

  function openTab(tab: DaochongWorkTab) {
    setActiveTab(tab);
    setCreateOpen(false);
  }

  function previewOnly() {
    setCreateOpen(false);
    setActiveTab("work");
  }

  return (
    <main className="mobile-work-page daochong-work-page">
      <section className="mobile-work-phone daochong-work-phone">
        <header className="mobile-work-header">
          <span aria-hidden="true" className="mobile-work-header-spacer" />
          <div>
            <h1>道冲元气</h1>
            <p>大爱归心工作平台 · {displayName}</p>
          </div>
          <span aria-hidden="true" className="mobile-work-header-spacer" />
        </header>

        <section className="daochong-hero">
          <div>
            <span>{heroMeta.eyebrow}</span>
            <h2>{heroMeta.title}</h2>
            <p>{displayName}，{heroMeta.copy}</p>
          </div>
          <DaochongBadge tone="forest">{agendaLabel}</DaochongBadge>
        </section>

        <section className="daochong-metrics-strip" aria-label="道冲摘要">
          {stats.map((stat) => (
            <article className="daochong-metric" key={stat.label}>
              <span>{stat.label}</span>
              <strong>{stat.value}</strong>
            </article>
          ))}
        </section>

        {summaryStatus !== "ready" ? (
          <section className={`daochong-read-status ${summaryStatus}`}>
            <span>{summaryStatus === "loading" ? "读取真实数据" : "真实数据暂未显示"}</span>
            <strong>{summaryStatus === "loading" ? "同步中" : "待核对"}</strong>
            {summaryStatus === "error" ? <p>{summaryError}</p> : null}
          </section>
        ) : null}

        <div className="daochong-tab-body">
          {activeTab === "agenda" ? (
            <>
              <section className="daochong-agenda-switch" aria-label="日程视角">
                <div>
                  <span>日程视角</span>
                  <strong>{agendaLabel}</strong>
                </div>
                <div className="daochong-segmented two" role="tablist" aria-label="日程时间范围">
                  {(Object.keys(daochongAgendaLabels) as DaochongAgendaScope[]).map((scope) => (
                    <button
                      aria-selected={agendaScope === scope}
                      className={agendaScope === scope ? "active" : ""}
                      key={scope}
                      onClick={() => setAgendaScope(scope)}
                      role="tab"
                      type="button"
                    >
                      {daochongAgendaLabels[scope]}
                    </button>
                  ))}
                </div>
              </section>

              <DaochongPanel title="全店预约" action={<a className="daochong-text-link" href={SCHEDULE_HREF}>看日历</a>}>
                <DaochongTimelineList items={summaryAppointments[agendaScope]} />
              </DaochongPanel>

              <section className="daochong-schedule-callout">
                <div>
                  <span>班表摘要</span>
                  <strong>{summaryRosterSummaries[agendaScope].title}</strong>
                  <p>{summaryRosterSummaries[agendaScope].detail}</p>
                </div>
                <button className="mobile-work-button primary" onClick={() => openTab("schedule")} type="button">看排班</button>
              </section>

              <DaochongPanel title="待办">
                <DaochongList items={summaryTodos} />
              </DaochongPanel>

              <DaochongPanel title="熊抱联动">
                <div className="daochong-segmented" role="tablist" aria-label="活动时间范围">
                  {(Object.keys(daochongActivities) as DaochongActivityScope[]).map((scope) => (
                    <button
                      aria-selected={activityScope === scope}
                      className={activityScope === scope ? "active" : ""}
                      key={scope}
                      onClick={() => setActivityScope(scope)}
                      role="tab"
                      type="button"
                    >
                      {daochongActivities[scope].label}
                    </button>
                  ))}
                </div>
                <DaochongList items={[activity.front[0], activity.kitchen[0]].filter((item): item is DaochongListItem => Boolean(item))} />
              </DaochongPanel>
            </>
          ) : null}

          {activeTab === "schedule" ? (
            <>
              <section className="daochong-agenda-switch" aria-label="班表视角">
                <div>
                  <span>班表视角</span>
                  <strong>{agendaLabel}</strong>
                </div>
                <div className="daochong-segmented two" role="tablist" aria-label="班表时间范围">
                  {(Object.keys(daochongAgendaLabels) as DaochongAgendaScope[]).map((scope) => (
                    <button
                      aria-selected={agendaScope === scope}
                      className={agendaScope === scope ? "active" : ""}
                      key={scope}
                      onClick={() => setAgendaScope(scope)}
                      role="tab"
                      type="button"
                    >
                      {daochongAgendaLabels[scope]}
                    </button>
                  ))}
                </div>
              </section>

              <DaochongRosterMap
                activity={summaryActivities[agendaScope]}
                appointments={summaryAppointments[agendaScope]}
                businessRosters={summaryBusinessRosters[agendaScope]}
                businessScope={rosterBusinessScope}
                onBusinessScopeChange={setRosterBusinessScope}
                rosterNotes={summaryRosterNotes[agendaScope]}
                rosterSupport={summaryRosterSupport[agendaScope]}
                scope={agendaScope}
              />
            </>
          ) : null}

          {activeTab === "work" ? (
            <DaochongPanel title={role === "principal" ? "管理工作" : "工作入口"}>
              <DaochongEntryGrid items={visibleWorkEntries} onFallback={previewOnly} />
            </DaochongPanel>
          ) : null}

          {activeTab === "mine" ? (
            <>
              <section className="daochong-profile-card">
                <span>{displayName.slice(0, 1)}</span>
                <div>
                  <strong>{displayName}</strong>
                  <p>道冲元气 · 大爱归心企业微信身份</p>
                </div>
              </section>
              <DaochongPanel title="我的">
                <DaochongEntryGrid
                  items={[
                    { title: "我的周报", detail: "查看、创建和更新周报", href: `${WEEKLY_REPORTS_HREF}&entry=profile` },
                    { title: "我的待办", detail: role === "principal" ? "审批、活动和团队提醒" : "纪要、截图和客户跟进" },
                    { title: "我的业绩", detail: "本人可见的目标和提成" },
                    { title: "账号身份", detail: "来自大爱归心企业微信登录" },
                  ]}
                  onFallback={previewOnly}
                />
              </DaochongPanel>
            </>
          ) : null}
        </div>

        {createOpen ? (
          <div className="mobile-work-create-sheet">
            <button aria-label="关闭新建面板" className="mobile-work-create-backdrop" onClick={() => setCreateOpen(false)} type="button" />
            <section className="mobile-work-create-panel daochong-create-panel" aria-label="新建道冲工作">
              <span className="mobile-work-handle" />
              <div className="mobile-work-section-title inline">
                <div>
                  <span>新建</span>
                  <strong>道冲工作</strong>
                </div>
                <button className="daochong-sheet-close" onClick={() => setCreateOpen(false)} type="button">
                  关闭
                </button>
              </div>
              <DaochongGroupedEntryGrid items={visibleCreateEntries} onFallback={previewOnly} />
            </section>
          </div>
        ) : null}

        <nav className="mobile-work-bottom-nav" aria-label="道冲底部导航">
          <button className={activeTab === "agenda" ? "active" : ""} onClick={() => openTab("agenda")} type="button">
            <Home size={24} />
            <span>日程</span>
          </button>
          <button className={activeTab === "schedule" ? "active" : ""} onClick={() => openTab("schedule")} type="button">
            <CalendarDays size={24} />
            <span>排班</span>
          </button>
          <button aria-expanded={createOpen} className="center" onClick={() => setCreateOpen((value) => !value)} type="button">
            <Plus size={34} />
            <span>新建</span>
          </button>
          <button className={activeTab === "work" ? "active" : ""} onClick={() => openTab("work")} type="button">
            <ReceiptText size={24} />
            <span>{role === "principal" ? "管理" : "工作"}</span>
          </button>
          <button className={activeTab === "mine" ? "active" : ""} onClick={() => openTab("mine")} type="button">
            <UserRound size={24} />
            <span>我的</span>
          </button>
        </nav>
      </section>
    </main>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "good" | "warn" | "danger" }) {
  return (
    <div className={`stat ${tone ?? ""}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function StatusPill({ status }: { status: PayrollDraft["validation"]["status"] }) {
  return (
    <span className={`status-pill ${status === "ready" ? "ready" : "blocked"}`}>
      {status === "ready" ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
      {statusLabel(status)}
    </span>
  );
}

function AppShell({
  children,
  route,
  user,
  onLogout,
}: {
  children: React.ReactNode;
  route: RouteState;
  user: CurrentUser | null;
  onLogout: () => void;
}) {
  const month = route.params.get("month") || currentMonth();
  const financeWorkbenchPath = IS_GRAY_RUNTIME ? "/finance/workbench-gray" : "/finance/workbench";
  const canUseFinanceWorkspace = canMaintainPayroll(user);
  const isFinancePreview = route.path === "/finance/workbench" || route.path === "/finance/workbench-gray";
  const isFinanceGray = IS_GRAY_RUNTIME || route.path === "/finance/workbench-gray";
  const financeNavGroups: { heading: string; items: { label: string; count?: string; active?: boolean }[] }[] = [
    {
      heading: "今天",
      items: [
        { label: "财务工作台", count: "12", active: true },
        { label: "待我处理", count: "8" },
        { label: "消息中心", count: "3" },
      ],
    },
    {
      heading: "月结与报表",
      items: [
        { label: "导入中心" },
        { label: "月结总览" },
        { label: "内账报表" },
        { label: "法定报表" },
      ],
    },
    {
      heading: "流水与报销",
      items: [
        { label: "银行流水", count: "5" },
        { label: "报销审核", count: "4" },
        { label: "未开发票" },
        { label: "报销规则" },
      ],
    },
    {
      heading: "薪资与业务结算",
      items: [
        { label: "发薪资条", count: "入口" },
        { label: "薪资批量填写" },
        { label: "薪资记录" },
        { label: "课程结算" },
        { label: "客户付款" },
      ],
    },
  ];
  const tabs = [
    { label: "手机工作", href: "/mobile/work", icon: Home },
    ...(canUseFinanceWorkspace ? [
      { label: "财务工作台", href: `${financeWorkbenchPath}?month=${month}`, icon: FileSpreadsheet },
      { label: "薪资工作台", href: `/payroll/batch?month=${month}`, icon: CircleDollarSign },
    ] : []),
  ];

  return (
    <div className={`app-shell ${isFinancePreview ? "finance-preview-shell" : ""}`}>
      <aside className="sidebar">
        <div className="brand-block">
          <span className="brand-mark">大</span>
          <div>
            <strong>{isFinancePreview ? "大爱归心管理平台" : "大愛歸心员工端"}</strong>
            <span>{isFinancePreview ? isFinanceGray ? "财务中心灰度预览" : "财务中心只读入口" : "薪资上传与发送"}</span>
          </div>
        </div>
        {isFinancePreview ? (
          <nav className="finance-sidebar-nav">
            {financeNavGroups.map((group) => (
              <div className="finance-sidebar-group" key={group.heading}>
                <span className="finance-sidebar-heading">{group.heading}</span>
                {group.items.map((item) => (
                  <button
                    className={`finance-sidebar-item ${item.active ? "active" : ""}`}
                    key={`${group.heading}-${item.label}`}
                    onClick={() => navigate(item.label === "发薪资条" ? `/payroll/batch?month=${month}` : `${financeWorkbenchPath}?month=${month}`)}
                    type="button"
                  >
                    <span>{item.label}</span>
                    {item.count ? <strong>{item.count}</strong> : null}
                  </button>
                ))}
              </div>
            ))}
          </nav>
        ) : (
          <nav className="nav-list">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const active = route.path === tab.href.split("?")[0] || route.path === "/finance/imports";
              return (
                <button
                  className={`nav-item ${active ? "active" : ""}`}
                  key={tab.href}
                  onClick={() => navigate(tab.href)}
                  type="button"
                >
                  <Icon size={18} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        )}
        <div className="sidebar-footer">
          <span>{userDisplayName(user)}</span>
          <button className="icon-text-button" onClick={onLogout} type="button" title="退出登录">
            <LogOut size={16} />
            退出
          </button>
        </div>
      </aside>
      <main className="main-area">{children}</main>
    </div>
  );
}

function buildFinanceFallbackSummary(month: string): FinanceWorkbenchSummary {
  return {
    month,
    generatedAt: new Date().toISOString(),
      connection: {
      status: "connected",
      label: "只读默认视图",
      note: "只读接口未返回前，先显示默认结构；成功读取后会替换为服务器只读摘要。",
      sources: [
        { label: "薪资草稿", status: "读取中", tone: "info" },
        { label: "薪资条记录", status: "读取中", tone: "info" },
        { label: "客户回款", status: "读取中", tone: "info" },
        { label: "报销审核", status: "待接入只读源", tone: "info" },
      ],
    },
    metrics: [
      { label: "本月结账进度", value: "--" },
      { label: "待处理事项", value: "--" },
      { label: "高风险异常", value: "--" },
      { label: "本月确认回款", value: "--" },
      { label: "发薪资条待确认", value: "--" },
    ],
    todos: [
      {
        tone: "info",
        label: "待确认",
        title: `发薪资条入口：${month} 薪资草稿`,
        detail: "只读摘要读取后会显示本月待确认人数。点右侧按钮进入薪资工作台。",
        action: "去发薪资条",
      },
    ],
    monthlySteps: [
      { index: "1", title: "基础档案", detail: "等待只读摘要", status: "读取中", tone: "info" },
      { index: "2", title: "月结文件导入", detail: "等待只读摘要", status: "读取中", tone: "info" },
      { index: "3", title: "回款与渠道结算", detail: "等待只读摘要", status: "读取中", tone: "info" },
      { index: "4", title: "生成内账报表", detail: "等待只读摘要", status: "读取中", tone: "info" },
    ],
    ledgerRows: [
      {
        date: "--",
        summary: "等待只读摘要",
        suggestion: "读取成功后显示回款和导入记录",
        status: "读取中",
        tone: "info",
        amount: "--",
      },
    ],
    quickEntries: [
      { title: "上传月结文件", detail: "灰度预览", action: "上传月结文件" },
      { title: "报销审核", detail: "待接入只读源", action: "报销审核" },
      { title: "发薪资条", detail: "进入薪资工作台", action: "goPayroll", highlight: true },
      { title: "薪资草稿", detail: "等待只读摘要", action: "薪资草稿" },
      { title: "未完成回款", detail: "等待只读摘要", action: "未完成回款" },
    ],
    drawer: {
      title: "等待只读摘要",
      amount: "--",
      matched: "读取成功后显示匹配建议",
      gap: "读取中",
    },
    safety: {
      impactPeriod: month,
      impactAmount: "--",
      notificationTargets: "--",
      currentStatus: "只读预览，不会提交",
    },
  };
}

function FinanceBadge({ tone, children }: { tone: string; children: React.ReactNode }) {
  return <span className={`finance-badge ${tone}`}>{children}</span>;
}

function FinanceMetric({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "good" | "warning" | "danger";
}) {
  return (
    <div className={`finance-metric ${tone ?? ""}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function FinanceWorkbenchGrayPage({ route, setToast }: { route: RouteState; setToast: (toast: Toast) => void }) {
  const month = route.params.get("month") || currentMonth();
  const isGrayPreview = IS_GRAY_RUNTIME || route.path === "/finance/workbench-gray";
  const [summary, setSummary] = useState<FinanceWorkbenchSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const data = summary ?? buildFinanceFallbackSummary(month);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    void loadFinanceWorkbenchSummary(month)
      .then((nextSummary) => {
        if (!active) return;
        setSummary(nextSummary);
      })
      .catch((caught) => {
        if (!active) return;
        setSummary(null);
        setError(caught instanceof Error ? caught.message : "读取只读财务摘要失败");
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [month]);

  function previewOnly(action: string) {
    setToast({
      tone: "info",
      message: `${isGrayPreview ? "灰度预览" : "只读入口"}：${action} 暂未接入正式操作，不会提交数据。`,
    });
  }

  function goPayroll() {
    navigate(`/payroll/batch?month=${month}`);
  }

  return (
    <section className="finance-preview-surface">
      <div className="finance-preview-topbar">
        <div className="finance-crumb">
          <strong>财务</strong>
          <span>/</span>
          <span>工作台</span>
          <span>/</span>
          <span>{data.month}</span>
        </div>
        <div className="finance-search">搜索订单、流水、员工、发票、客户手机号</div>
        <div className="finance-top-chips">
          <span>本月</span>
          <span>办公室</span>
          <span className="preview">{loading ? "读取中" : error ? "只读读取失败" : isGrayPreview ? "灰度预览" : data.connection.label}</span>
        </div>
      </div>

      <section className="finance-hero">
        <div>
          <span className="finance-kicker">一屏看清财务今天要做什么</span>
          <h1>财务工作台</h1>
          <p>把月结、导入、流水匹配、报销审核、薪资发布放到一个入口里，先显示缺口和待办，再进入具体页面处理。</p>
        </div>
        <div className="finance-hero-actions">
          <button className="finance-button salary" onClick={goPayroll} type="button">发薪资条</button>
          <button className="finance-button primary" onClick={() => previewOnly("处理待办")} type="button">处理待办</button>
          <button className="finance-button warning" onClick={() => previewOnly("查看月结缺口")} type="button">查看月结缺口</button>
          <button className="finance-button" onClick={() => previewOnly("上传流水")} type="button">上传流水</button>
          <button className="finance-button" onClick={() => previewOnly("生成报表")} type="button">生成报表</button>
        </div>
      </section>

      <section className={`finance-data-strip ${error ? "danger" : ""}`}>
        <div>
          <strong>{error ? "只读数据读取失败" : data.connection.label}</strong>
          <p>{error || data.connection.note}</p>
        </div>
        <div className="finance-source-list">
          {data.connection.sources.map((source) => (
            <FinanceBadge key={source.label} tone={source.tone}>{source.label}：{source.status}</FinanceBadge>
          ))}
        </div>
      </section>

      <section className="finance-metric-grid">
        {data.metrics.map((metric) => (
          <FinanceMetric
            key={metric.label}
            label={metric.label}
            tone={metric.tone}
            value={metric.value}
          />
        ))}
      </section>

      <section className="finance-preview-grid">
        <div className="finance-stack">
          <section className="finance-panel">
            <div className="finance-panel-head">
              <h2>今日优先处理</h2>
              <span>按风险和月结影响排序</span>
            </div>
            <div className="finance-todo-list">
              {data.todos.map((todo) => (
                <div className="finance-todo-row" key={todo.title}>
                  <FinanceBadge tone={todo.tone}>{todo.label}</FinanceBadge>
                  <div>
                    <strong>{todo.title}</strong>
                    <p>{todo.detail}</p>
                  </div>
                  <button className={todo.action === "去发薪资条" ? "finance-button salary" : todo.tone === "danger" ? "finance-button primary" : "finance-button"} onClick={todo.action === "去发薪资条" ? goPayroll : () => previewOnly(todo.action)} type="button">
                    {todo.action}
                  </button>
                </div>
              ))}
            </div>
          </section>

          <section className="finance-panel">
            <div className="finance-panel-head">
              <h2>流水与导入只读摘要</h2>
              <span>点击一行，右侧显示处理建议</span>
            </div>
            <div className="finance-table-wrap">
              <table className="finance-table">
                <thead>
                  <tr>
                    <th>日期</th>
                    <th>摘要</th>
                    <th>系统建议</th>
                    <th>状态</th>
                    <th className="amount">金额</th>
                  </tr>
                </thead>
                <tbody>
                  {data.ledgerRows.map((row) => (
                    <tr key={`${row.date}-${row.summary}`}>
                      <td>{row.date}</td>
                      <td>{row.summary}</td>
                      <td>{row.suggestion}</td>
                      <td><FinanceBadge tone={row.tone}>{row.status}</FinanceBadge></td>
                      <td className="amount">{row.amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="finance-panel">
            <div className="finance-panel-head">
              <h2>常用入口</h2>
              <span>少找菜单，直接进入高频任务</span>
            </div>
            <div className="finance-quick-grid">
              {data.quickEntries.map((entry) => (
                <button className={`finance-quick ${entry.highlight ? "highlight" : ""}`} key={entry.title} onClick={entry.action === "goPayroll" ? goPayroll : () => previewOnly(entry.title)} type="button">
                  <strong>{entry.title}</strong>
                  <span>{entry.detail}</span>
                </button>
              ))}
            </div>
          </section>
        </div>

        <div className="finance-stack">
          <section className="finance-panel">
            <div className="finance-panel-head">
              <h2>月结缺口清单</h2>
              <span>缺什么就点哪里</span>
            </div>
            <div className="finance-step-list">
              {data.monthlySteps.map((step) => (
                <div className={`finance-step ${step.tone}`} key={step.index}>
                  <span className="finance-step-index">{step.index}</span>
                  <div>
                    <strong>{step.title}</strong>
                    <p>{step.detail}</p>
                  </div>
                  <FinanceBadge tone={step.tone}>{step.status}</FinanceBadge>
                </div>
              ))}
            </div>
          </section>

          <section className="finance-panel">
            <div className="finance-panel-head">
              <h2>右侧详情抽屉示意</h2>
              <span>减少页面跳转</span>
            </div>
            <div className="finance-drawer-card">
              <div><span>当前项目</span><strong>{data.drawer.title}</strong></div>
              <div><span>金额</span><strong>{data.drawer.amount}</strong></div>
              <div><span>系统匹配</span><strong>{data.drawer.matched}</strong></div>
              <div><span>缺口</span><strong>{data.drawer.gap}</strong></div>
              <div className="finance-drawer-actions">
                <button className="finance-button primary" onClick={() => previewOnly("确认匹配")} type="button">确认匹配</button>
                <button className="finance-button" onClick={() => previewOnly("退回补票")} type="button">退回补票</button>
                <button className="finance-button" onClick={() => previewOnly("标异常")} type="button">标异常</button>
              </div>
            </div>
          </section>

          <section className="finance-panel">
            <div className="finance-panel-head">
              <h2>操作安全提示</h2>
              <span>付款、发布前二次确认</span>
            </div>
            <div className="finance-drawer-card compact">
              <div><span>影响期间</span><strong>{data.safety.impactPeriod}</strong></div>
              <div><span>影响金额</span><strong>{data.safety.impactAmount}</strong></div>
              <div><span>通知对象</span><strong>{data.safety.notificationTargets}</strong></div>
              <div><span>当前状态</span><strong>{data.safety.currentStatus}</strong></div>
            </div>
          </section>
        </div>
      </section>
    </section>
  );
}

function MobileExpensePage({ user }: { user: CurrentUser }) {
  const [form, setForm] = useState<MobileExpenseFormState>({
    title: "",
    category: "道冲活动/物料",
    amount: "",
    summary: "",
    payee: userDisplayName(user),
    accountHint: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ tone: "success" | "danger"; text: string } | null>(null);

  function updateField(field: keyof MobileExpenseFormState, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const amount = Number(form.amount);
    if (!form.title.trim()) {
      setMessage({ tone: "danger", text: "请填写报销标题。" });
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      setMessage({ tone: "danger", text: "请填写正确金额。" });
      return;
    }
    if (!form.summary.trim()) {
      setMessage({ tone: "danger", text: "请填写报销说明。" });
      return;
    }

    setSubmitting(true);
    setMessage(null);
    try {
      const result = await apiFetch<ExpenseClaimMutationResponse>("/expense-claims", {
        method: "POST",
        body: JSON.stringify({
          title: form.title.trim(),
          applicant: userDisplayName(user),
          department: user.department ?? "道冲元气",
          category: form.category.trim() || "道冲活动/物料",
          amount,
          summary: form.summary.trim(),
          payee: form.payee.trim() || userDisplayName(user),
          accountHint: form.accountHint.trim(),
        }),
      });
      const latest = result.workspace?.expenseClaims?.[0];
      setMessage({
        tone: "success",
        text: latest
          ? `已提交报销记录：${latest.title}。后续由财务在大爱归心后台复核。`
          : "已提交报销记录，后续由财务在大爱归心后台复核。",
      });
      setForm({
        title: "",
        category: "道冲活动/物料",
        amount: "",
        summary: "",
        payee: userDisplayName(user),
        accountHint: "",
      });
    } catch (error) {
      setMessage({ tone: "danger", text: error instanceof Error ? error.message : "提交失败，请稍后重试。" });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mobile-work-page daochong-work-page">
      <section className="mobile-work-phone daochong-work-phone">
        <header className="mobile-work-header">
          <a aria-label="回到道冲" className="mobile-work-plain-button" href="/mobile/work/daochong">道冲</a>
          <div>
            <h1>报销申请</h1>
            <p>大爱归心工作平台 · {userDisplayName(user)}</p>
          </div>
          <span aria-hidden="true" className="mobile-work-header-spacer" />
        </header>

        <section className="daochong-hero">
          <div>
            <span>财务</span>
            <h2>提交报销记录</h2>
            <p>提交后进入大爱归心财务记录，票据附件仍按企业微信/财务要求补齐。</p>
          </div>
          <DaochongBadge tone="forest">真实</DaochongBadge>
        </section>

        {message ? <div className={`mobile-expense-message ${message.tone}`}>{message.text}</div> : null}

        <form className="mobile-expense-form" onSubmit={submit}>
          <label>
            <span>报销标题</span>
            <input
              value={form.title}
              onChange={(event) => updateField("title", event.target.value)}
              placeholder="例如：道冲活动物料"
            />
          </label>
          <label>
            <span>类型</span>
            <select value={form.category} onChange={(event) => updateField("category", event.target.value)}>
              <option>道冲活动/物料</option>
              <option>客户接待</option>
              <option>差旅交通</option>
              <option>办公采购</option>
              <option>其他/待财务归类</option>
            </select>
          </label>
          <label>
            <span>金额</span>
            <input
              inputMode="decimal"
              value={form.amount}
              onChange={(event) => updateField("amount", event.target.value)}
              placeholder="0.00"
            />
          </label>
          <label>
            <span>收款人</span>
            <input value={form.payee} onChange={(event) => updateField("payee", event.target.value)} />
          </label>
          <label>
            <span>账号备注</span>
            <input
              value={form.accountHint}
              onChange={(event) => updateField("accountHint", event.target.value)}
              placeholder="可填银行卡尾号、微信、支付宝等"
            />
          </label>
          <label>
            <span>说明</span>
            <textarea
              value={form.summary}
              onChange={(event) => updateField("summary", event.target.value)}
              placeholder="写清楚用途、对应活动或客户"
              rows={4}
            />
          </label>
          <button className="mobile-work-button primary" disabled={submitting} type="submit">
            {submitting ? "提交中" : "提交报销"}
          </button>
        </form>
      </section>
    </main>
	  );
	}

type MobileHomeKind = "founder" | "daochong" | "bearhug" | "ecommerce" | "finance" | "sales" | "general";

type MobileHomeTask = {
  title: string;
  description: string;
  badge?: string;
  tone?: DaochongTone;
  action?: {
    label: string;
    href: string;
  };
};

function isEcotechMergedEcommerceUser(user: CurrentUser | null | undefined) {
  if (!user) {
    return false;
  }

  const text = normalizedText(
    user.displayName,
    user.name,
    user.username,
    user.loginAccount,
    user.wecomUserId,
    user.wecomName,
    user.department,
    user.title,
    user.roleCode,
    user.roleName,
  );
  const isTanxi = hasAnyText(text, ["tanxi", "谭喜", "譚喜"]);
  const isEcotechMarket = hasAnyText(text, ["洄归", "洄歸", "生态科技", "生態科技", "ecotech", "ecotech_manager"])
    && hasAnyText(text, ["市场", "市場", "market"]);

  return isTanxi || isEcotechMarket;
}

function getMobileHomeKind(user: CurrentUser) {
  const text = normalizedText(
    user.displayName,
    user.name,
    user.username,
    user.loginAccount,
    user.wecomUserId,
    user.department,
    user.title,
    user.roleCode,
    user.roleName,
  );
  const daochongRole = getDaochongEntryRole(user);
  if (hasAnyText(text, ["崔以达", "崔以達", "edwardtsuei", "创办人", "創辦人"]) || user.roleCode === "SUPER_ADMIN") {
    return "founder";
  }
  if (daochongRole) {
    return "daochong";
  }
  if (user.roleCode === "SALES" || user.roleCode === "SALES_MANAGER" || hasAnyText(text, ["线下渠道", "渠道销售", "销售总监", "sales_manager"])) {
    return "sales";
  }
  if (hasAnyText(text, ["熊抱", "bearhug", "主厨", "主廚", "后厨", "後廚", "greatchef"])) {
    return "bearhug";
  }
  if (isEcotechMergedEcommerceUser(user) || hasAnyText(text, ["电商", "電商", "ecommerce", "yinxiaojuan", "运营", "運營"])) {
    return "ecommerce";
  }
  if (hasAnyText(text, ["财务", "財務", "finance", "limeng", "周立猛"])) {
    return "finance";
  }
  return "general";
}

function getMobileHomeDepartment(user: CurrentUser, kind: MobileHomeKind) {
  if (kind === "founder") return "大爱归心 · 创办人";
  if (kind === "general") return user.department ?? "大爱归心";
  if (kind === "ecommerce" && isEcotechMergedEcommerceUser(user)) return "电商部 · 洄归生态项目";
  if (kind === "ecommerce") return "电商部";
  if (kind === "bearhug") return "熊抱大地";
  if (kind === "finance") return "财务";
  if (kind === "sales") return "洄归生态 · 线下渠道销售";
  return "道冲元气";
}

function getMobileHomeSubtitle(user: CurrentUser, kind: MobileHomeKind) {
  if (kind === "founder") {
    return "崔以达，上午好";
  }
  if (kind === "ecommerce" && isEcotechMergedEcommerceUser(user)) {
    return `${userDisplayName(user)} · 市场拓展 / 电商协作`;
  }
  if (kind === "sales") {
    return `${userDisplayName(user)} · 客户录入 / 跟进 / 周复盘`;
  }
  return `${userDisplayName(user)} · ${user.title ?? user.roleName}`;
}

function getMobileTodayMeta(kind: MobileHomeKind, user?: CurrentUser | null) {
  const todayLabel = formatMobileTodayLabel();
  if (kind === "sales") {
    return {
      kicker: "今日 · 线下渠道销售",
      title: todayLabel,
      description: "先看今天要跟进的客户，再补录新客户和本周遗漏。",
    };
  }
  if (kind === "ecommerce" && isEcotechMergedEcommerceUser(user)) {
    return {
      kicker: "今日 · 电商部",
      title: todayLabel,
      description: "洄归生态项目已并入电商协作，先看直播拓展、周报同步和项目沟通。",
    };
  }
  if (kind === "ecommerce") {
    return {
      kicker: "今日 · 电商部",
      title: todayLabel,
      description: "看本周数据、平台动作和项目协作。",
    };
  }
  if (kind === "bearhug") {
    return {
      kicker: "今日 · 熊抱大地",
      title: todayLabel,
      description: "前厅后厨分开看，活动和协作集中处理。",
    };
  }
  if (kind === "finance") {
    return {
      kicker: "今日 · 财务",
      title: todayLabel,
      description: "先看报销、复核、薪资和月结缺口。",
    };
  }
  if (kind === "daochong") {
    return {
      kicker: "今日 · 道冲元气",
      title: todayLabel,
      description: "道冲与熊抱班表互通，方便互相支援。",
    };
  }
  return {
    kicker: "今日 · 大爱归心",
    title: todayLabel,
    description: "先看今天安排，再处理周报和协作。",
  };
}

function getMobileHomeTasks(kind: MobileHomeKind, user?: CurrentUser | null): MobileHomeTask[] {
  if (kind === "sales") {
    return [
      { title: "今日跟进", description: "到期、逾期和下一步待办集中处理。", badge: "今日", tone: "forest", action: { label: "进入", href: "/mobile/work/customers" } },
      { title: "新客户录入", description: "一次录入公司、联系人、需求和首次跟进。", badge: "录入", tone: "blue", action: { label: "新建", href: "/mobile/work/customers?view=intake" } },
      { title: "本周复盘", description: "检查客户覆盖、商机推进和记录完整度。", badge: "周检", tone: "neutral", action: { label: "检查", href: "/mobile/work/customers?view=weekly" } },
    ];
  }
  if (kind === "ecommerce") {
    if (isEcotechMergedEcommerceUser(user)) {
      return [
        { title: "本周周报", description: "提交洄归生态项目进展，并纳入电商部周报口径。", badge: "优先", tone: "forest", action: { label: "填写", href: WEEKLY_REPORTS_HREF } },
        { title: "电商周报", description: "查看平台数据、复核状态和发布进度。", badge: "数据", tone: "blue", action: { label: "进入", href: ECOM_WEEKLY_HREF } },
        { title: "项目沟通", description: "直播拓展、ODM 事项和跨部门协作记录。", badge: "协作", tone: "neutral", action: { label: "记录", href: PROJECT_DISCUSSION_HREF } },
      ];
    }
    return [
      { title: "电商周报", description: "上传数据复盘 + 本周完成 / 下周计划。", badge: "周报", tone: "forest", action: { label: "进入", href: ECOM_WEEKLY_HREF } },
      { title: "个人周报", description: "本周总结、下周计划和遗留承接。", badge: "填写", tone: "neutral", action: { label: "填写", href: WEEKLY_REPORTS_HREF } },
      { title: "项目讨论", description: "跨部门事项进入协作纪要。", badge: "协作", tone: "blue", action: { label: "查看", href: PROJECT_DISCUSSION_HREF } },
    ];
  }
  if (kind === "bearhug") {
    return [
      { title: "熊抱前厅", description: "今日暂无在岗。", badge: "前厅", tone: "forest", action: { label: "班表", href: SHIFT_ROSTER_HREF } },
      { title: "熊抱后厨", description: "后厨安排、备餐和活动协作。", badge: "后厨", tone: "neutral", action: { label: "记录", href: "/mobile/work" } },
      { title: "今日活动", description: "活动记录和现场事项集中查看。", badge: "活动", tone: "blue", action: { label: "查看", href: "/mobile/work" } },
    ];
  }
  if (kind === "finance") {
    return [
      { title: "报销审核", description: "票据、金额和付款信息复核。", badge: "财务", tone: "forest", action: { label: "处理", href: "/finance/expenses" } },
      { title: "薪资工作", description: "薪资条、通知和历史记录。", badge: "薪资", tone: "neutral", action: { label: "进入", href: "/payroll/batch" } },
      { title: "月结缺口", description: "流水、回款和月结进度。", badge: "月结", tone: "blue", action: { label: "查看", href: "/finance/dashboard" } },
    ];
  }
  if (kind === "daochong") {
    return [
      { title: "道冲元气", description: "预约、排班、活动提醒和待补事项。", badge: "道冲", tone: "forest", action: { label: "查看", href: buildDaochongEntryHref("teacher") } },
      { title: "全店排班", description: "查看今日和本周排班。", badge: "班表", tone: "neutral", action: { label: "查看", href: SHIFT_ROSTER_HREF } },
      { title: "周报填写", description: "查看本周提交状态。", badge: "周报", tone: "blue", action: { label: "填写", href: WEEKLY_REPORTS_HREF } },
    ];
  }
  return [
    { title: "今日工作", description: "查看今天要处理的事项。", badge: "今日", tone: "forest", action: { label: "查看", href: "/mobile/work" } },
    { title: "周报填写", description: "本周总结与下周计划。", badge: "周报", tone: "neutral", action: { label: "填写", href: WEEKLY_REPORTS_HREF } },
    { title: "项目讨论", description: "跨部门协作和会议纪要。", badge: "协作", tone: "blue", action: { label: "查看", href: PROJECT_DISCUSSION_HREF } },
  ];
}

function MobileHomeTaskList({ items }: { items: MobileHomeTask[] }) {
  return (
    <div className="mobile-office-list">
      {items.map((item) => (
        <article className="mobile-office-item" key={item.title}>
          <div>
            <strong>{item.title}</strong>
            <p>{item.description}</p>
          </div>
          <div className="mobile-office-item-actions">
            {item.badge ? <span className={`mobile-restore-pill ${item.tone ?? "neutral"}`}>{item.badge}</span> : null}
            {item.action ? <a className="mobile-restore-action" href={item.action.href}>{item.action.label}</a> : null}
          </div>
        </article>
      ))}
    </div>
  );
}

function MobileEcommerceFocusPanel({ user }: { user: CurrentUser }) {
  const mergedProfile = isEcotechMergedEcommerceUser(user);
  const focusItems = mergedProfile
    ? [
        { label: "身份", value: "电商部" },
        { label: "项目", value: "洄归生态" },
        { label: "重点", value: "ODM 直播" },
      ]
    : [
        { label: "工作口径", value: "电商运营周报" },
        { label: "数据复盘", value: "平台 / GMV / 售后" },
        { label: "协作记录", value: "项目讨论与纪要" },
      ];

  return (
    <section className="mobile-ecom-focus-card" aria-label="电商协作重点">
      <div className="mobile-ecom-focus-top">
        <div>
          <span className="mobile-ecom-focus-kicker">
            {mergedProfile ? "已并入电商协作" : "电商运营"}
          </span>
          <h3>{mergedProfile ? "先把市场动作同步到周报" : "先看数据，再写复盘"}</h3>
          <p>
            {mergedProfile
              ? "保留洄归项目语境，同时按电商部的周报、复盘和协作节奏推进。"
              : "运营数据、周报复盘和跨部门协作放在同一个入口里处理。"}
          </p>
        </div>
        <span className="mobile-ecom-focus-tag">电商</span>
      </div>
      <div className="mobile-ecom-focus-metrics">
        {focusItems.map((item) => (
          <article key={item.label}>
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </article>
        ))}
      </div>
      <div className="mobile-ecom-focus-actions">
        <a href={WEEKLY_REPORTS_HREF}>写周报</a>
        <a href={ECOM_WEEKLY_HREF}>看电商数据</a>
      </div>
    </section>
  );
}

function MobileRestoreOverview({ kind, user }: { kind: MobileHomeKind; user: CurrentUser }) {
  const meta = getMobileTodayMeta(kind, user);
  const mergedEcommerceProfile = kind === "ecommerce" && isEcotechMergedEcommerceUser(user);
  if (kind === "founder") {
    return (
      <>
        <section className="mobile-founder-hero">
          <div className="mobile-founder-title-row">
            <div>
              <span>大爱归心 · 创办人</span>
              <h2>崔以达，上午好</h2>
              <p>本周 7/6 - 7/12 · 第 28 周</p>
            </div>
            <em>等待财务数据</em>
          </div>
          <div className="mobile-founder-metrics">
            {["总营收", "净利", "现金余额"].map((label) => (
              <article key={label}>
                <strong>-</strong>
                <span>{label}</span>
              </article>
            ))}
          </div>
          <div className="mobile-founder-lanes">
            {["光的家园", "归心之旅", "洄归生态", "道冲元气"].map((label) => (
              <article key={label}>
                <span>{label}</span>
                <strong>待接入</strong>
              </article>
            ))}
          </div>
          <a href="/finance/dashboard">按公司主体看报表 ›</a>
        </section>
        <section className="mobile-office-section mobile-office-section-primary">
          <div className="panel-head">
            <h3>道冲老师团队</h3>
            <a className="mobile-restore-link" href={buildDaochongEntryHref("teacher")}>进入道冲 ›</a>
          </div>
          <MobileHomeTaskList
            items={[
              { title: "服务体感 · 本周", description: "2 服务场次 · ¥0 本周收款 · 0 体感已填。", badge: "本周", tone: "forest" },
              { title: "日历速览", description: "今日暂无已发布班表，可在班表页发布本周排班。", badge: "今天", tone: "neutral" },
            ]}
          />
        </section>
      </>
    );
  }

  return (
    <>
      <section className="mtro" aria-label="今日工作总览">
        <div className="mtro-head">
          <span className="mtro-kicker">{meta.kicker}</span>
          <h2>{meta.title}</h2>
          <p>{meta.description}</p>
        </div>
        {kind === "ecommerce" ? <MobileEcommerceFocusPanel user={user} /> : null}
        <section className="mobile-office-section mobile-office-section-primary mobile-office-section-today">
          <MobileHomeTaskList items={getMobileHomeTasks(kind, user)} />
        </section>
      </section>
      <section className="mobile-office-section mobile-office-section-primary">
        <div className="panel-head">
          <h3>待处理</h3>
          <span className="mobile-restore-pill neutral">{kind === "bearhug" ? "2" : kind === "ecommerce" ? (mergedEcommerceProfile ? "2" : "1") : "0"}</span>
        </div>
        <MobileHomeTaskList
          items={[
            kind === "bearhug"
              ? { title: "团队周报", description: "创办人和主管从这里查看团队周报与待点评成员。", badge: "审阅", tone: "forest", action: { label: "去查看", href: WEEKLY_REPORTS_HREF } }
              : kind === "ecommerce"
                ? mergedEcommerceProfile
                  ? { title: "洄归项目同步", description: "把直播拓展、ODM 进展和下周动作写入本周周报。", badge: "待写", tone: "forest", action: { label: "去填写", href: WEEKLY_REPORTS_HREF } }
                  : { title: "电商周报", description: "整理本周数据复盘和下周计划。", badge: "待写", tone: "forest", action: { label: "去填写", href: WEEKLY_REPORTS_HREF } }
                : { title: "暂无待处理", description: "项目讨论、周报批阅等真实任务出现后会在这里汇总。", badge: "-", tone: "neutral" },
          ]}
        />
      </section>
    </>
  );
}

function getFounderWeekMeta(date = new Date()) {
  const monday = new Date(date);
  const dayOffset = (date.getDay() + 6) % 7;
  monday.setDate(date.getDate() - dayOffset);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const weekDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = weekDate.getUTCDay() || 7;
  weekDate.setUTCDate(weekDate.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(weekDate.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((weekDate.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${monday.getMonth() + 1}/${monday.getDate()} - ${sunday.getMonth() + 1}/${sunday.getDate()} · 第 ${week} 周`;
}

function getMobileGreeting(date = new Date()) {
  const hour = date.getHours();
  if (hour < 12) return "上午好";
  if (hour < 18) return "下午好";
  return "晚上好";
}

function MobileOriginalBottomNav({
  scheduleHref = SHIFT_ROSTER_HREF,
  scheduleLabel = "班表",
  workHref = "/mobile/work",
}: {
  scheduleHref?: string;
  scheduleLabel?: string;
  workHref?: string;
}) {
  return (
    <nav className="bottom-nav has-center-fab mobile-office-bottom" aria-label="员工底部导航">
      <a className="bottom-item is-active" href="/mobile">
        <Home className="bottom-icon" aria-hidden="true" />
        <span>今日</span>
      </a>
      <a className="bottom-item" href={scheduleHref}>
        <CalendarDays className="bottom-icon" aria-hidden="true" />
        <span>{scheduleLabel}</span>
      </a>
      <button className="bottom-fab" type="button" onClick={() => { window.location.href = CREATE_WEEKLY_HREF; }} aria-label="新建">
        <Plus className="bottom-fab-icon" aria-hidden="true" />
        <span>新建</span>
      </button>
      <a className="bottom-item" href={workHref}>
        <ClipboardList className="bottom-icon" aria-hidden="true" />
        <span>工作</span>
      </a>
      <a className="bottom-item" href="/mobile/me">
        <UserRound className="bottom-icon" aria-hidden="true" />
        <span>我的</span>
      </a>
    </nav>
  );
}

function MobileFounderOriginalHomePage({ user }: { user: CurrentUser }) {
  const [pendingCustomerReviews, setPendingCustomerReviews] = useState<EmployeeNotification[]>([]);
  const name = userDisplayName(user);
  const weekMeta = getFounderWeekMeta();

  useEffect(() => {
    let active = true;
    void apiFetch<EmployeeNotificationList>("/notifications?page=1&pageSize=50&channel=all")
      .then((response) => {
        if (!active) return;
        setPendingCustomerReviews(
          response.items.filter(
            (item) =>
              item.type === "SALES_CUSTOMER_CREATED_REVIEW" &&
              item.relatedType === "CUSTOMER" &&
              item.sendChannel === "SYSTEM" &&
              !item.readAt &&
              Boolean(item.relatedId),
          ),
        );
      })
      .catch(() => {
        if (active) setPendingCustomerReviews([]);
      });
    return () => {
      active = false;
    };
  }, [user.id]);

  return (
    <main className="fh-page route-enter">
      <section className="fh-hero">
        <div className="fh-hero-top">
          <div>
            <div className="fh-eyebrow">大爱归心 · 创办人</div>
            <h2 className="fh-hello">{name}，{getMobileGreeting()}</h2>
            <div className="fh-week">本周 {weekMeta}</div>
          </div>
          <div className="fh-data-badge">等待财务数据</div>
        </div>
        <div className="fh-hero-chips">
          {["总营收", "净利", "现金余额"].map((label) => (
            <div className="fh-chip" key={label}>
              <b>-</b>
              <span>{label}</span>
            </div>
          ))}
        </div>
        <div className="fh-entities">
          {["光的家园", "归心之旅", "洄归生态", "道冲元气"].map((label) => (
            <div className="fh-entity" key={label}>
              <span>{label}</span>
              <b>待接入</b>
            </div>
          ))}
        </div>
        <a className="fh-hero-drill" href="/finance/dashboard">按公司主体看报表 ›</a>
      </section>

      <div className="fh-section-label fh-section-label-first">
        客户录入待核对 <span className="fh-badge">{pendingCustomerReviews.length}</span>
        <a className="fh-more" href="/mobile/work/customers?view=weekly">查看客户 ›</a>
      </div>
      {pendingCustomerReviews.length ? (
        <section className="fh-review-list">
          {pendingCustomerReviews.slice(0, 4).map((item) => (
            <a
              href={`/mobile/work/customers/review?customerId=${encodeURIComponent(item.relatedId ?? "")}`}
              key={item.id}
            >
              <span className="fh-review-icon">客</span>
              <span>
                <strong>{item.title}</strong>
                <small>{item.content}</small>
              </span>
              <ChevronRight size={18} />
            </a>
          ))}
        </section>
      ) : (
        <section className="fh-empty fh-empty-compact">
          <b>当前没有待核对的客户</b>
          <span>孙小松新增客户后，将在这里直接显示。</span>
        </section>
      )}

      <div className="fh-section-label">
        管理协同
        <a className="fh-more" href="/work-management/home?from=founder-mobile">进入工作台 ›</a>
      </div>
      <section className="fh-quick">
        {[
          ["周", "团队周报", FOUNDER_WEEKLY_REPORTS_HREF],
          ["财", "财务看板", FOUNDER_FINANCE_DASHBOARD_HREF],
          ["会", "会议纪要", FOUNDER_MEETING_MINUTES_HREF],
          ["课", "课程项目", FOUNDER_COURSE_PROJECTS_HREF],
          ["班", "统一班表", FOUNDER_SHIFT_ROSTER_HREF],
          ["员", "成员管理", FOUNDER_MEMBERS_HREF],
        ].map(([icon, label, href]) => (
          <a className="fh-qmini" href={href} key={label}>
            <span className="fh-qico">{icon}</span>
            <span className="fh-qlabel">{label}</span>
          </a>
        ))}
      </section>

      <div className="fh-section-label">
        日历速览
        <a className="fh-more" href={FOUNDER_SHIFT_ROSTER_HREF}>进入统一日历 ›</a>
      </div>
      <section className="fh-cal">
        <div className="fh-cal-row">
          <span className="fh-cal-tag ct-dc">统一日历</span>
          <div className="fh-cal-txt">
            <b>查看各团队日程与班表</b>
            <span>课程、门店、道冲等日程统一预览。</span>
          </div>
          <span className="fh-cal-when">今天</span>
        </div>
      </section>

      <MobileOriginalBottomNav
        scheduleHref={FOUNDER_SHIFT_ROSTER_HREF}
        scheduleLabel="行事历"
        workHref="/work-management/home?from=founder-mobile"
      />

      <div className="fh-section-label">异常与风险</div>
      <section className="fh-empty">
        <b>暂无异常</b>
        <span>目前没有来自真实数据源的逾期、待补或高风险提醒。</span>
      </section>

      <div className="fh-section-label">
        部门脉动
        <a className="fh-more" href={WEEKLY_REPORTS_HREF}>全部部门 ›</a>
      </div>
      <section className="fh-empty">
        <b>暂无部门周报汇总</b>
        <span>负责人提交真实周报后，这里会显示各部门最新进展。</span>
      </section>

      <div className="fh-section-label">本周摘要</div>
      <section className="fh-digest">
        <div className="fh-digest-src"><span className="fh-ai">AI</span>等待真实周报</div>
        <p>暂无可汇总的真实周报内容。部门负责人提交并完成批阅后，会在这里生成本周摘要。</p>
        <a className="fh-digest-more" href={WEEKLY_REPORTS_HREF}>查看完整摘要与会议纪要 ›</a>
      </section>

      <div className="fh-section-label">业务入口</div>
      <section className="fh-quick">
        {[
          ["客", "洄归客户", FOUNDER_ECOTECH_CUSTOMERS_HREF],
          ["课", "课程内容组", FOUNDER_COURSE_PROJECTS_HREF],
          ["道", "道冲元气", "/mobile/work/daochong?role=teacher"],
        ].map(([icon, label, href]) => (
          <a className="fh-qmini" href={href} key={label}>
            <span className="fh-qico">{icon}</span>
            <span className="fh-qlabel">{label}</span>
          </a>
        ))}
      </section>
    </main>
  );
}

function MobileDaochongOriginalHomePage({ user }: { user: CurrentUser }) {
  const isPrincipal = getDaochongEntryRole(user) === "chengcheng";
  const name = userDisplayName(user);
  const subtitle = isPrincipal ? `${name} · 道冲元气主理人` : `${name} · 老师`;
  const todayLabel = formatMobileTodayLabel();
  const pendingCount = isPrincipal ? "2" : "1";
  const moreCount = isPrincipal ? "31" : "29";
  const rosterRows = [
    ["慧心", "早班 10:00-18:00"],
    ["子青", "早班 10:00-18:00"],
    ["燕子", "晚班 12:00-20:00"],
    ["觉心", "晚班 12:00-20:00"],
  ];
  const activities = [
    ["预约状态已更新", "陈女士 complete · 05/30 12:25"],
    ["预约状态已更新", "陈女士 arrive · 05/30 12:24"],
    ["道冲 workspace 已初始化", "预约、收款与储值 smoke 基线已建立。 · 05/30 12:24"],
    ["克里希那穆提讨论会包场", "6月20-21日包场，9:00开始；含20日中晚餐、21日中餐。 · 06/20 09:00"],
  ];
  const todoItems: Array<{ title: string; description: string; scope: "个人" | "共享" }> = [];
  return (
    <main className="mobile-restore-page mobile-original-home">
      <section className="mobile-office-home route-enter">
        <header className="mobile-office-head">
          <div>
            <span className="mobile-office-kicker">道冲元气</span>
            <h2>今日工作</h2>
            <p>{subtitle}</p>
          </div>
          <a className="mobile-office-alert" href="/messages" aria-label="未读消息">
            <span>提醒</span>
            <strong>0</strong>
          </a>
        </header>

        <section className="mtro" aria-label="今日跨部门排班总览">
          <div className="mtro-head">
            <span className="mtro-kicker">今日 · 道冲元气</span>
            <h2>{todayLabel}</h2>
            <p>道冲与熊抱班表互通，方便互相支援</p>
          </div>
          <section className="mtro-block is-own">
            <div className="mtro-block-head">
              <span className="mtro-dept-tag dao">道冲</span>
              <b>道冲元气</b>
              <span className="mtro-cnt">在岗 4</span>
            </div>
            {rosterRows.map(([person, time]) => (
              <div className="mtro-duty" key={person}>
                <span className="who">{person}</span>
                <span className="tm">{time}</span>
              </div>
            ))}
          </section>
          <section className="mtro-block is-own">
            <div className="mtro-block-head">
              <span className="mtro-dept-tag dao">道冲</span>
              <b>道冲元气 · 今日预约</b>
              <span className="mtro-cnt">0 单</span>
            </div>
            <p className="mtro-empty">今日暂无预约。</p>
          </section>
          <section className="mtro-block">
            <div className="mtro-block-head">
              <span className="mtro-dept-tag act">活动</span>
              <b>今日活动</b>
              <span className="mtro-cnt">4 项</span>
            </div>
            {activities.map(([title, description]) => (
              <div className="mtro-act" key={title + description}>
                <b>{title}</b>
                <span>{description}</span>
              </div>
            ))}
          </section>
          <section className="mtro-block">
            <div className="mtro-block-head">
              <span className="mtro-dept-tag front">前厅</span>
              <b>熊抱前厅</b>
              <span className="mtro-cnt">在岗 0</span>
            </div>
            <p className="mtro-empty">今日暂无在岗。</p>
          </section>
          <section className="mtro-block">
            <div className="mtro-block-head">
              <span className="mtro-dept-tag kit">后厨</span>
              <b>熊抱后厨</b>
              <span className="mtro-cnt">在岗 0</span>
            </div>
            <p className="mtro-empty">今日暂无在岗。</p>
          </section>
          <section className="mtro-block mtro-todo">
            <div className="mtro-todo-head">
              <div className="mtro-todo-title">
                <span aria-hidden="true" />
                <b>待办</b>
              </div>
              <em>{todoItems.length}</em>
            </div>

            {todoItems.length > 0 ? (
              <div className="mtro-todo-list">
                {todoItems.map((item) => (
                  <article className="mtro-todo-item" key={item.title}>
                    <span className="mtro-todo-check" aria-hidden="true" />
                    <div>
                      <b>{item.title}</b>
                      <span>{item.description}</span>
                    </div>
                    <em className={item.scope === "共享" ? "team" : undefined}>{item.scope}</em>
                  </article>
                ))}
              </div>
            ) : (
              <div className="mtro-todo-empty">
                <span className="mtro-todo-empty-icon" aria-hidden="true" />
                <div>
                  <b>暂无待办</b>
                  <span>有需要记录的事项，可添加为个人或部门共享。</span>
                </div>
              </div>
            )}

            <div className="mtro-todo-compose">
              <div className="mtro-todo-scope" role="group" aria-label="待办范围">
                <button className="is-active" type="button">个人</button>
                <button type="button">部门共享</button>
              </div>
              <div className="mtro-todo-add-row">
                <input placeholder="写一件待办事项..." readOnly />
                <button type="button" aria-label="新增待办">+</button>
              </div>
            </div>
          </section>
        </section>

        <MobileOriginalBottomNav workHref={buildDaochongEntryHref(isPrincipal ? "chengcheng" : "teacher")} />

        <section className="mobile-original-weekly">
          <div className="mobile-original-weekly-top">
            <span>周报</span>
            <em>今天</em>
          </div>
          <h3>周报填写</h3>
          <p>本周要提交。</p>
          <strong>周报待填</strong>
          <div>
            <a href={CREATE_WEEKLY_HREF}>填写</a>
            <a href={WEEKLY_REPORTS_HREF}>看班表</a>
          </div>
        </section>

        <nav className="mobile-core-actions mobile-original-quick" aria-label="道冲快捷操作">
          {[
            { title: "班表", detail: "今天暂无班表", href: SHIFT_ROSTER_HREF, icon: CalendarDays, tone: "forest" },
            { title: "工作", detail: "活动记录", href: buildDaochongEntryHref(isPrincipal ? "chengcheng" : "teacher"), icon: ClipboardList, tone: "forest" },
            { title: "申请", detail: "请假、报销、问题", href: EXPENSE_HREF, icon: ReceiptText, tone: "earth" },
          ].map((item) => {
            const ActionIcon = item.icon;
            return (
              <a className={`mobile-core-action ${item.tone}`} href={item.href} key={item.title}>
                <ActionIcon className="mobile-core-icon" aria-hidden="true" />
                <span>{item.title}</span>
                <small>{item.detail}</small>
              </a>
            );
          })}
        </nav>

        <section className="mobile-office-section mobile-office-section-primary mobile-original-pending">
          <div className="panel-head">
            <h3>待处理</h3>
            <span className="mobile-restore-pill neutral">{pendingCount}</span>
          </div>
          <MobileHomeTaskList
            items={[
              isPrincipal
                ? { title: "团队周报", description: "创办人和主管从这里查看团队周报与待点评成员。", badge: "审阅", tone: "forest", action: { label: "去查看", href: WEEKLY_REPORTS_HREF } }
                : { title: "暂无待处理", description: "先按班表走起，有事再处理。", badge: "-", tone: "neutral", action: { label: "看班表", href: SHIFT_ROSTER_HREF } },
            ]}
          />
        </section>

        <details className="mobile-home-more">
          <summary>
            <div>
              <span>更多工作信息</span>
              <strong>班表、记录、全部入口</strong>
            </div>
            <em>{moreCount}</em>
          </summary>
        </details>
      </section>
    </main>
  );
}

function MobileRestoredHomePage({ user }: { user: CurrentUser }) {
  const [createOpen, setCreateOpen] = useState(false);
  const kind = getMobileHomeKind(user);
  if (kind === "founder") {
    return <MobileFounderOriginalHomePage user={user} />;
  }
  if (kind === "daochong") {
    return <MobileDaochongOriginalHomePage user={user} />;
  }
  if (kind === "sales") {
    return <SalesMobileHome user={user} />;
  }
  const department = getMobileHomeDepartment(user, kind);
  const subtitle = getMobileHomeSubtitle(user, kind);
  const isEcommerceHome = kind === "ecommerce";
  const workHref = isEcommerceHome ? ECOM_WEEKLY_HREF : "/mobile/work";
  const createItems = isEcommerceHome
    ? [
        { title: "创建周报", detail: "本周总结、下周计划", href: CREATE_WEEKLY_HREF, icon: FileSpreadsheet, tone: "forest" },
        { title: "电商周报", detail: "数据复盘、发布状态", href: ECOM_WEEKLY_HREF, icon: ClipboardList, tone: "blue" },
        { title: "会议纪要", detail: "结论、待办、归档", href: MEETING_MINUTES_HREF, icon: ClipboardList, tone: "neutral" },
        { title: "项目讨论", detail: "协作沟通和记录", href: PROJECT_DISCUSSION_HREF, icon: UsersRound, tone: "neutral" },
      ]
    : [
        { title: "创建周报", detail: "本周总结、下周计划", href: CREATE_WEEKLY_HREF, icon: FileSpreadsheet, tone: "forest" },
        { title: "报销申请", detail: "票据、金额、说明", href: EXPENSE_HREF, icon: ReceiptText, tone: "forest" },
        { title: "会议纪要", detail: "结论、待办、归档", href: MEETING_MINUTES_HREF, icon: ClipboardList, tone: "blue" },
        { title: "项目讨论", detail: "协作沟通和记录", href: PROJECT_DISCUSSION_HREF, icon: UsersRound, tone: "neutral" },
      ];
  const quickActions = isEcommerceHome
    ? [
        { title: "周报", detail: "本周提交", href: WEEKLY_REPORTS_HREF, icon: FileSpreadsheet },
        { title: "电商", detail: "数据复盘", href: ECOM_WEEKLY_HREF, icon: ClipboardList },
        { title: "协作", detail: "项目记录", href: PROJECT_DISCUSSION_HREF, icon: UsersRound },
      ]
    : [
        { title: "班表", detail: "今天看班表", href: SHIFT_ROSTER_HREF, icon: CalendarDays },
        { title: "工作", detail: "活动记录", href: workHref, icon: ClipboardList },
        { title: "申请", detail: "报销、问题", href: EXPENSE_HREF, icon: ReceiptText },
      ];

  return (
    <main className="mobile-restore-page">
      <section className={`mobile-office-home route-enter ${isEcommerceHome ? "mobile-office-home-ecommerce" : ""}`}>
        <header className="mobile-office-head">
          <div>
            <span className="mobile-office-kicker">{department}</span>
            <h2>今日工作</h2>
            <p>{subtitle}</p>
          </div>
          <a className="mobile-office-alert" href="/messages" aria-label="未读消息">
            <span>提醒</span>
            <strong>0</strong>
          </a>
        </header>

        <MobileRestoreOverview kind={kind} user={user} />

        <nav className="mobile-core-actions" aria-label="手机快捷操作">
          {quickActions.map((item) => {
            const Icon = item.icon;
            return (
              <a className="mobile-core-action forest" href={item.href} key={`${item.title}-${item.href}`}>
                <Icon className="mobile-core-icon" aria-hidden="true" />
                <span>{item.title}</span>
                <small>{item.detail}</small>
              </a>
            );
          })}
        </nav>

        <details className="mobile-home-more">
          <summary>
            <div>
              <span>更多工作信息</span>
              <strong>{kind === "ecommerce" ? "电商工作台、周报、协作入口" : kind === "bearhug" ? "前厅、后厨、活动记录" : "班表、记录、全部入口"}</strong>
            </div>
            <em>{kind === "bearhug" ? "23" : kind === "ecommerce" ? "20" : "18"}</em>
          </summary>
          <div className="mobile-home-more-body">
            <section className="mobile-home-more-group">
              <div className="panel-head">
                <h3>常用入口</h3>
                <span className="mobile-restore-pill neutral">4</span>
              </div>
              <MobileHomeTaskList
                items={[
                  { title: "周报工作台", description: "本周总结、下周计划和历史记录。", badge: "周报", tone: "forest", action: { label: "进入", href: WEEKLY_REPORTS_HREF } },
                  ...(isEcommerceHome
                    ? [{ title: "电商周报", description: "平台数据、复核状态和发布记录。", badge: "电商", tone: "blue" as DaochongTone, action: { label: "进入", href: ECOM_WEEKLY_HREF } }]
                    : []),
                  { title: "项目讨论", description: "跨部门协作和会议纪要。", badge: "协作", tone: "blue", action: { label: "查看", href: PROJECT_DISCUSSION_HREF } },
                  { title: "报销申请", description: "提交票据、金额和说明。", badge: "申请", tone: "neutral", action: { label: "提交", href: EXPENSE_HREF } },
                ]}
              />
            </section>
          </div>
        </details>

        {createOpen ? (
          <div className="mobile-work-create-sheet">
            <button aria-label="关闭新建面板" className="mobile-work-create-backdrop" onClick={() => setCreateOpen(false)} type="button" />
            <section className="mobile-work-create-panel" aria-label="新建">
              <span className="mobile-work-handle" />
              <div className="mobile-work-section-title">
                <span>新建</span>
                <strong>快速创建</strong>
              </div>
              <nav className="mobile-work-entry-list" aria-label="新建工作">
                {createItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <a className={`mobile-work-entry ${item.tone}`} href={item.href} key={item.title}>
                      <Icon size={22} />
                      <span>{item.title}</span>
                      <small>{item.detail}</small>
                    </a>
                  );
                })}
              </nav>
            </section>
          </div>
        ) : null}

        <nav className="mobile-restore-bottom-nav" aria-label="手机工作导航">
          <a className="active" href="/mobile">
            <Home size={24} />
            <span>今日</span>
          </a>
          <a href={SHIFT_ROSTER_HREF}>
            <CalendarDays size={24} />
            <span>班表</span>
          </a>
          <button aria-expanded={createOpen} className="center" onClick={() => setCreateOpen((value) => !value)} type="button">
            <Plus size={34} />
            <span>新建</span>
          </button>
          <a href={workHref}>
            <ReceiptText size={24} />
            <span>工作</span>
          </a>
          <a href="/mobile/me">
            <UserRound size={24} />
            <span>我的</span>
          </a>
        </nav>
      </section>
    </main>
  );
}

function MobileWorkPage({ route, user, onLogout }: { route: RouteState; user: CurrentUser; onLogout: () => void }) {
  const [createOpen, setCreateOpen] = useState(false);
  if (route.path === "/mobile/work/daochong") {
    return <DaochongWorkPage route={route} user={user} />;
  }

  const daochongRole = getDaochongEntryRole(user);
  const daochongHref = daochongRole ? buildDaochongEntryHref(daochongRole) : "";
  const isDaochongOperator = daochongRole === "chengcheng" || daochongRole === "teacher";
  const isProfileView = route.path === "/mobile/me";
  const weeklyReportsHref = WEEKLY_REPORTS_HREF;
  const createWeeklyHref = CREATE_WEEKLY_HREF;
  const daochongRoleLabel = daochongRole === "chengcheng"
    ? "主理人"
    : daochongRole === "finance"
      ? "财务"
      : "老师";
  const priorityTitle = daochongRole === "finance"
      ? "财务复核入口"
      : daochongRole
        ? "道冲元气"
        : "暂无待办";
  const priorityCopy = daochongRole === "chengcheng"
    ? "全店预约、全店班表、前厅后厨、活动目标和审批待办。"
    : daochongRole === "finance"
      ? "充值复核、凭证查看、财务汇总和异常跟进。"
      : daochongRole
        ? "全店预约、全店班表、活动提醒和待补事项。"
        : "活动、项目、周报按优先级显示。";
  const priorityTags = daochongRole === "finance"
    ? ["充值复核", "凭证", "财务汇总"]
    : daochongRole
      ? ["全店预约", "全店班表", "活动", "待办"]
      : ["周报", "日程", "项目"];
  const stats = [
    { label: "待处理", value: isDaochongOperator ? "待接" : "0" },
    { label: "项目", value: isDaochongOperator ? "待接" : "0" },
    { label: "未读", value: "0" },
    { label: "日程", value: isDaochongOperator ? "真实" : "0" },
  ];
  const moreItems = [
	    daochongRole
	      ? {
	          title: "道冲元气",
	          detail: "日程、排班、待办",
          href: daochongHref,
          icon: BriefcaseBusiness,
          tone: "forest",
        }
	      : null,
	    { title: "日程安排", detail: "全店预约和班表", href: SCHEDULE_HREF, icon: CalendarDays, tone: "blue" },
	    { title: "项目讨论", detail: "协作纪要", href: PROJECT_DISCUSSION_HREF, icon: ClipboardList, tone: "neutral" },
	    { title: "团队入口", detail: "前厅后厨", href: "/work-management", icon: UsersRound, tone: "amber" },
	  ].filter((item): item is NonNullable<typeof item> => Boolean(item));
  const createItems = [
    { title: "创建周报", detail: "本周总结、下周计划", href: createWeeklyHref, icon: FileSpreadsheet, tone: "forest" },
    { title: "报销申请", detail: "票据、金额、说明", href: EXPENSE_HREF, icon: ReceiptText, tone: "amber" },
    { title: "会议纪要", detail: "结论、待办、归档", href: MEETING_MINUTES_HREF, icon: ClipboardList, tone: "blue" },
    { title: "项目讨论", detail: "协作沟通和记录", href: PROJECT_DISCUSSION_HREF, icon: UsersRound, tone: "neutral" },
  ];
  const profileItems = [
    { title: "我的周报", detail: "草稿、提交、历史记录", href: weeklyReportsHref, icon: FileSpreadsheet, tone: "forest" },
    { title: daochongRole ? "全店排班" : "我的排班", detail: "今日、本周、本月", href: SHIFT_ROSTER_HREF, icon: CalendarDays, tone: "blue" },
    { title: "我的申请", detail: "报销、请假、补资料", href: EXPENSE_HREF, icon: ReceiptText, tone: "amber" },
    daochongRole
      ? { title: "道冲元气", detail: "日程、排班、周报", href: daochongHref, icon: BriefcaseBusiness, tone: "forest" }
      : null,
  ].filter((item): item is NonNullable<typeof item> => Boolean(item));

	  return (
	    <main className="mobile-work-page">
	      <section className="mobile-work-phone">
		        <header className="mobile-work-header">
          <button aria-label="返回" className="mobile-work-plain-button" onClick={() => navigate("/mobile")} type="button">
            返回
          </button>
		          <div>
		            <h1>大爱归心工作平台</h1>
		            <p>{userDisplayName(user)} · {user.department ?? "工作入口"} · {daochongRole ? daochongRoleLabel : user.roleName}</p>
		          </div>
		          <button aria-label="退出" className="mobile-work-plain-button" onClick={onLogout} type="button">
		            <LogOut size={20} />
		          </button>
	        </header>

	        <section className="mobile-work-stat-grid" aria-label="工作摘要">
	          {stats.map((stat) => (
	            <article className="mobile-work-stat" key={stat.label}>
	              <span>{stat.label}</span>
	              <strong>{stat.value}</strong>
	            </article>
	          ))}
	        </section>

        {isProfileView ? (
          <section className="mobile-work-panel">
            <div className="mobile-work-section-title inline">
              <div>
                <span>我的</span>
                <strong>个人工作</strong>
              </div>
              <em>{profileItems.length}</em>
            </div>
            <nav className="mobile-work-entry-list" aria-label="我的工作">
              {profileItems.map((item) => {
                const Icon = item.icon;
                return (
                  <a className={`mobile-work-entry ${item.tone}`} href={item.href} key={item.title}>
                    <Icon size={22} />
                    <span>{item.title}</span>
                    <small>{item.detail}</small>
                  </a>
                );
              })}
            </nav>
          </section>
        ) : (
          <>
            <section className="mobile-work-panel">
              <div className="mobile-work-section-title">
                <span>当前优先</span>
                <strong>{daochongRole ? "预约信息" : "暂无待办"}</strong>
              </div>
              {daochongRole ? (
                <article className="mobile-work-priority is-daochong">
                  <div>
                    <span className="mobile-work-kicker">道冲元气</span>
                    <h2>{priorityTitle}</h2>
                    <p>{priorityCopy}</p>
                    <div className="mobile-work-priority-tags">
                      {priorityTags.map((tag) => <span key={tag}>{tag}</span>)}
                    </div>
                  </div>
                  <a className="mobile-work-button primary" href={daochongHref}>打开</a>
                </article>
              ) : (
                <article className="mobile-work-priority">
                  <div>
                    <span className="mobile-work-kicker">当前优先</span>
                    <h2>{priorityTitle}</h2>
                    <p>{priorityCopy}</p>
                    <div className="mobile-work-priority-tags">
                      {priorityTags.map((tag) => <span key={tag}>{tag}</span>)}
                    </div>
                  </div>
                </article>
              )}
              <article className="mobile-work-submission">
                <div>
                  <strong>周报入口</strong>
                  <span>进入大爱归心周报系统查看真实状态</span>
                </div>
                <span className="mobile-work-pill">真实入口</span>
                <a className="mobile-work-button secondary" href={weeklyReportsHref}>进入</a>
              </article>
            </section>

            <section className="mobile-work-panel">
              <div className="mobile-work-section-title inline">
                <div>
                  <span>更多工作</span>
                  <strong>{moreItems.map((item) => item.title).join("、")}</strong>
                </div>
                <em>{moreItems.length}</em>
              </div>
              <nav className="mobile-work-entry-list" aria-label="更多工作">
                {moreItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <a className={`mobile-work-entry ${item.tone}`} href={item.href} key={item.title}>
                      <Icon size={22} />
                      <span>{item.title}</span>
                      <small>{item.detail}</small>
                    </a>
                  );
                })}
              </nav>
            </section>
          </>
        )}

        {createOpen ? (
          <div className="mobile-work-create-sheet">
            <button aria-label="关闭新建面板" className="mobile-work-create-backdrop" onClick={() => setCreateOpen(false)} type="button" />
            <section className="mobile-work-create-panel" aria-label="新建">
              <span className="mobile-work-handle" />
              <div className="mobile-work-section-title">
                <span>新建</span>
                <strong>快速创建</strong>
              </div>
              <nav className="mobile-work-entry-list" aria-label="新建工作">
                {createItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <a className={`mobile-work-entry ${item.tone}`} href={item.href} key={item.title}>
                      <Icon size={22} />
                      <span>{item.title}</span>
                      <small>{item.detail}</small>
                    </a>
                  );
                })}
              </nav>
            </section>
          </div>
        ) : null}
	        <nav className="mobile-work-bottom-nav" aria-label="手机工作导航">
	          <a className={route.path === "/mobile" ? "active" : undefined} href="/mobile">
	            <Home size={24} />
	            <span>今日</span>
	          </a>
          <a href={SHIFT_ROSTER_HREF}>
            <CalendarDays size={24} />
            <span>班表</span>
          </a>
          <button aria-expanded={createOpen} className="center" onClick={() => setCreateOpen((value) => !value)} type="button">
            <Plus size={34} />
            <span>新建</span>
          </button>
			          <a className={route.path === "/mobile/work" || route.path === "/mobile/work/daochong" ? "active" : undefined} href={isDaochongOperator ? daochongHref : "/mobile/work"}>
	            <ReceiptText size={24} />
	            <span>工作</span>
	          </a>
          <a className={isProfileView ? "active" : undefined} href="/mobile/me">
            <UserRound size={24} />
            <span>我的</span>
          </a>
        </nav>
      </section>
    </main>
  );
}

function LoginPage({ onLogin }: { onLogin: (user: CurrentUser) => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const payload = await login(username, password);
      setAuth(payload);
      onLogin(payload.user);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "登录失败");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-screen">
      <form className="login-panel" onSubmit={submit}>
        <span className="panel-kicker">Payroll Access</span>
        <h1>进入员工端</h1>
        <label>
          <span>账号</span>
          <input autoComplete="username" onChange={(event) => setUsername(event.target.value)} value={username} />
        </label>
        <label>
          <span>密码</span>
          <input autoComplete="current-password" onChange={(event) => setPassword(event.target.value)} type="password" value={password} />
        </label>
        {error ? <p className="form-error">{error}</p> : null}
        <button className="primary-button" disabled={loading || !username || !password} type="submit">
          {loading ? <Loader2 className="spin" size={18} /> : <LogIn size={18} />}
          登录
        </button>
      </form>
    </div>
  );
}

function NoPermission({ user }: { user: CurrentUser | null }) {
  return (
    <section className="empty-state">
      <ShieldCheck size={32} />
      <h2>当前账号没有薪资维护权限</h2>
      <p>{userDisplayName(user)} 需要 FINANCE 角色或 action.payroll.publish 权限后才能上传、发布和记录通知。</p>
    </section>
  );
}

function PayrollBatchPage({ route, user, setToast }: { route: RouteState; user: CurrentUser | null; setToast: (toast: Toast) => void }) {
  const [month, setMonth] = useState(route.params.get("month") || currentMonth());
  const [file, setFile] = useState<File | null>(null);
  const [draft, setDraft] = useState<PayrollDraft | null>(null);
  const [salarySlips, setSalarySlips] = useState<SalarySlip[]>([]);
  const [notifyLogs, setNotifyLogs] = useState<SalaryNotifyLog[]>([]);
  const [reviewedOriginal, setReviewedOriginal] = useState(false);
  const [confirmedRecipients, setConfirmedRecipients] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState("");

  const lists = useMemo(() => notifyLists(draft?.rows ?? []), [draft]);
  const ready = draftIsReady(draft);
  const canPublish = ready && reviewedOriginal && confirmedRecipients && !publishing;

  useEffect(() => {
    const routeMonth = route.params.get("month") || currentMonth();
    if (routeMonth !== month) {
      setMonth(routeMonth);
    }
  }, [route.params, month]);

  async function refresh(nextMonth = month) {
    setLoading(true);
    setError("");
    try {
      const batch = await loadDraftBatch(nextMonth);
      const nextDraft = draftFromBatch(batch);
      setDraft(nextDraft);
      const publishBatchId = nextDraft?.publishBatchId ?? batch?.publishBatchId;
      const [slips, logs] = await Promise.all([
        listSalarySlips(nextMonth, publishBatchId),
        listNotifyLogs(nextMonth, publishBatchId),
      ]);
      setSalarySlips(slips.data);
      setNotifyLogs(logs.data);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "读取薪资数据失败");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh(month);
  }, [month]);

  async function handleFile(nextFile: File | null) {
    setFile(nextFile);
    setError("");
    if (!nextFile) {
      return;
    }
    setUploading(true);
    setReviewedOriginal(false);
    setConfirmedRecipients(false);
    try {
      const parsed = await parseSalaryFile(nextFile, month);
      await saveDraftBatch(parsed.draft, { updatedBy: userDisplayName(user) });
      setDraft(parsed.draft);
      setSalarySlips([]);
      setNotifyLogs([]);
      setToast({
        tone: parsed.supportedPreview ? "success" : "warning",
        message: parsed.supportedPreview ? "已保存薪资表草稿，下方已生成核对数据。" : "已记录文件信息，请转换为 XLSX 或 CSV 后发布。",
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "读取薪资表失败");
    } finally {
      setUploading(false);
    }
  }

  async function publish() {
    if (!draft || !canPublish) {
      return;
    }
    setPublishing(true);
    setError("");
    try {
      const syncResponse = await syncSalarySlips(draft, user);
      const sendResponse = await sendSalaryWecomNotifications(draft, user, { dryRun: IS_GRAY_RUNTIME });
      await saveDraftBatch(draft, {
        publishedAt: new Date().toISOString(),
        notifyStatus: sendResponse.status,
        excelReviewedAt: new Date().toISOString(),
        updatedBy: userDisplayName(user),
      });
      setToast({
        tone: sendResponse.ok ? "success" : "warning",
        message: `已发布 ${syncResponse.createdCount + syncResponse.updatedCount} 条薪资条；${sendResponse.message}`,
      });
      await refresh(month);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "发布失败");
    } finally {
      setPublishing(false);
    }
  }

  function changeMonth(value: string) {
    setMonth(value);
    navigate(`/payroll/batch?month=${value}`);
  }

  return (
    <section className="work-surface">
      <div className="page-header">
        <div>
          <span className="panel-kicker">Payroll Workbench</span>
          <h1>薪资工作台</h1>
        </div>
        <div className="header-actions">
          <label className="month-field">
            <span>月份</span>
            <input onChange={(event) => changeMonth(event.target.value)} type="month" value={month} />
          </label>
          <button className="secondary-button" onClick={() => void refresh(month)} type="button">
            <RefreshCw size={16} />
            刷新
          </button>
        </div>
      </div>

      {error ? <div className="alert danger"><AlertTriangle size={18} />{error}</div> : null}

      <section className="panel import-panel">
        <div className="panel-title-row">
          <div>
            <h2>上传薪资表</h2>
            <p>支持 CSV、XLSX；上传后同页生成预览、名单和发布确认区。</p>
          </div>
          <span className="type-chip">薪资表</span>
        </div>
        <label className="upload-zone">
          <UploadCloud size={34} />
          <strong>{file ? file.name : "选择薪资表文件"}</strong>
          <span>.csv / .xlsx / .xls</span>
          <input
            accept=".csv,.xlsx,.xls"
            onChange={(event) => void handleFile(event.target.files?.[0] ?? null)}
            type="file"
          />
        </label>
        {uploading ? <div className="loading-row"><Loader2 className="spin" size={18} />正在解析并保存草稿...</div> : null}
      </section>

      {loading ? (
        <div className="loading-row"><Loader2 className="spin" size={20} />正在读取薪资批次...</div>
      ) : draft ? (
        <>
          <div className="summary-grid">
            <Stat label="明细行数" value={`${draft.rows.length}`} />
            <Stat label="实发合计" value={`¥${formatAmount(netAmountTotal(draft.rows))}`} />
            <Stat label="可企微通知" value={`${lists.delivered.length} 人`} tone="good" />
            <Stat label="跳过通知" value={`${lists.skipped.length} 人`} tone={lists.skipped.length ? "warn" : undefined} />
          </div>

          <section className="panel">
            <div className="panel-title-row">
              <div>
                <h2>{draft.fileName}</h2>
                <p>发布批次：{draft.publishBatchId}</p>
              </div>
              <StatusPill status={draft.validation.status} />
            </div>
            <ValidationSummary draft={draft} />
            <div className="review-row">
              <label className="checkbox-line">
                <input checked={reviewedOriginal} onChange={(event) => setReviewedOriginal(event.target.checked)} type="checkbox" />
                我已核对原始薪资表
              </label>
              <label className="checkbox-line">
                <input checked={confirmedRecipients} onChange={(event) => setConfirmedRecipients(event.target.checked)} type="checkbox" />
                我已确认通知名单
              </label>
              <button className="primary-button" disabled={!canPublish} onClick={() => void publish()} type="button">
                {publishing ? <Loader2 className="spin" size={18} /> : <Send size={18} />}
                {IS_GRAY_RUNTIME ? "发布并预览企微" : "发布并发送企微"}
              </button>
            </div>
          </section>

          <PayrollRowsTable rows={draft.rows} />
          <NotifyPanel delivered={lists.delivered} skipped={lists.skipped} />
        </>
      ) : (
        <section className="empty-state">
          <FileSpreadsheet size={36} />
          <h2>当前月份还没有薪资导入草稿</h2>
          <p>在上方选择薪资表文件后，会直接在本页完成核对、发布和通知记录。</p>
        </section>
      )}

      <HistoryPanel logs={notifyLogs} salarySlips={salarySlips} />
    </section>
  );
}

function ValidationSummary({ draft }: { draft: PayrollDraft }) {
  const validation = draft.validation;
  const issues = [
    ...validation.missingRequiredHeaders.map((item) => `缺少表头：${item}`),
    ...validation.invalidAmountRows.map((row) => `第 ${row.rowNumber} 行 ${row.teacherName} 金额异常：${row.amountErrors.join("、")}`),
    ...validation.missingIdentityRows.map((row) => `第 ${row.rowNumber} 行 ${row.teacherName} 缺少员工身份`),
    ...validation.unresolvedRows.map((row) => `第 ${row.rowNumber} 行 ${row.teacherName} 差异未处理`),
  ];

  return (
    <div className="validation-list">
      {validation.warnings.map((warning) => (
        <div className="alert warning" key={warning}><AlertTriangle size={16} />{warning}</div>
      ))}
      {issues.length === 0 ? (
        <div className="alert success"><CheckCircle2 size={16} />预览已通过发布门禁。</div>
      ) : issues.map((issue) => (
        <div className="alert danger" key={issue}><AlertTriangle size={16} />{issue}</div>
      ))}
    </div>
  );
}

function DeductionCell({
  amount,
  items,
}: {
  amount: number;
  items?: PayrollDraft["rows"][number]["deductionItems"];
}) {
  return (
    <div className="deduction-cell">
      <span>¥{formatAmount(amount)}</span>
      {items?.length ? (
        <small>
          {items.map((item) => `${item.label} ¥${formatAmount(item.amount)}`).join(" / ")}
        </small>
      ) : null}
    </div>
  );
}

function PayrollRowsTable({ rows }: { rows: PayrollDraft["rows"] }) {
  if (rows.length === 0) {
    return null;
  }

  return (
    <section className="panel">
      <div className="panel-title-row">
        <div>
          <h2>薪资明细</h2>
          <p>按明确身份字段发布，姓名不作为授权依据。</p>
        </div>
      </div>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>姓名</th>
              <th>身份</th>
              <th>部门</th>
              <th>应发</th>
              <th>提成</th>
              <th>分润</th>
              <th>个人承担合计</th>
              <th>实发</th>
              <th>差异</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.rowNumber}-${row.teacherId}`}>
                <td>{row.teacherName}</td>
                <td>{row.teacherId}</td>
                <td>{row.department}</td>
                <td>¥{formatAmount(row.grossAmount)}</td>
                <td>¥{formatAmount(row.commissionAmount)}</td>
                <td>¥{formatAmount(row.profitSharingAmount)}</td>
                <td><DeductionCell amount={row.deductionAmount} items={row.deductionItems} /></td>
                <td className="amount-strong">¥{formatAmount(row.netAmount)}</td>
                <td>{row.differenceStatus === "resolved" ? "已处理" : "未处理"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function NotifyPanel({ delivered, skipped }: { delivered: ReturnType<typeof notifyLists>["delivered"]; skipped: ReturnType<typeof notifyLists>["skipped"] }) {
  return (
    <section className="panel two-column-panel">
      <div>
        <h2>通知名单</h2>
        <ul className="person-list">
          {delivered.map((person) => (
            <li key={person.id}>
              <CheckCircle2 size={16} />
              <span>{person.name}</span>
              <strong>{person.userid}</strong>
            </li>
          ))}
          {delivered.length === 0 ? <li className="muted-row">暂无可通知人员</li> : null}
        </ul>
      </div>
      <div>
        <h2>跳过名单</h2>
        <ul className="person-list">
          {skipped.map((person) => (
            <li key={person.id}>
              <AlertTriangle size={16} />
              <span>{person.name}</span>
              <strong>{person.reason}</strong>
            </li>
          ))}
          {skipped.length === 0 ? <li className="muted-row">没有跳过人员</li> : null}
        </ul>
      </div>
    </section>
  );
}

function HistoryPanel({ salarySlips, logs }: { salarySlips: SalarySlip[]; logs: SalaryNotifyLog[] }) {
  return (
    <section className="panel history-panel">
      <div className="panel-title-row">
        <div>
          <h2>发布追溯</h2>
          <p>按月份和发布批次读取正式薪资条与通知记录。</p>
        </div>
        <Search size={18} />
      </div>
      <div className="history-grid">
        <div>
          <strong>正式薪资条</strong>
          <span>{salarySlips.length} 条</span>
        </div>
        <div>
          <strong>通知记录</strong>
          <span>{logs.length} 条</span>
        </div>
      </div>
    </section>
  );
}

export function App() {
  const [route, setRoute] = useState(readRoute);
  const [user, setUser] = useState<CurrentUser | null>(() => getCurrentUser());
  const [toast, setToast] = useState<Toast | null>(null);
  const mobileWorkPreviewUser = buildMobileWorkPreviewUser(route);
  const effectiveUser = mobileWorkPreviewUser ?? user;

  useEffect(() => {
    const listener = () => setRoute(readRoute());
    window.addEventListener("popstate", listener);
    return () => window.removeEventListener("popstate", listener);
  }, []);

  useEffect(() => {
    if (!toast) {
      return;
    }
    const timer = window.setTimeout(() => setToast(null), 5000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    const daochongRole = getDaochongEntryRole(effectiveUser);
    if (isDaochongAutoEntryPath(route.path) && (daochongRole === "chengcheng" || daochongRole === "teacher")) {
      navigate(buildDaochongEntryHref(daochongRole));
    }
  }, [effectiveUser, route.path]);

  useEffect(() => {
    if (!effectiveUser) {
      return;
    }

    const redirect = getAuthenticatedRouteRedirect(effectiveUser, route.path);
    if (redirect) {
      navigate(redirect);
    }
  }, [effectiveUser, route.path]);

  useEffect(() => {
    if (effectiveUser || !isWecomBrowser()) {
      return;
    }

    window.location.assign(buildWecomLoginBridgeUrl(route));
  }, [effectiveUser, route]);

  function logout() {
    clearAuth();
    setUser(null);
    navigate("/login");
  }

  if (!effectiveUser) {
    return <LoginPage onLogin={(nextUser) => {
      setUser(nextUser);
      navigate(getPostLoginPath(nextUser, route.path, window.location.search));
    }} />;
  }

  const authenticatedRedirect = getAuthenticatedRouteRedirect(effectiveUser, route.path);
  if (authenticatedRedirect) {
    return null;
  }

  const daochongRole = getDaochongEntryRole(effectiveUser);
  if (isDaochongAutoEntryPath(route.path) && (daochongRole === "chengcheng" || daochongRole === "teacher")) {
    return null;
  }

  if (route.path === "/mobile/expense") {
    return <MobileExpensePage user={effectiveUser} />;
  }

  if (route.path === "/mobile") {
    return <MobileRestoredHomePage user={effectiveUser} />;
  }

  if (route.path === "/mobile/work/customers") {
    const requestedView = route.params.get("view");
    const initialView = requestedView === "intake" || requestedView === "followup" || requestedView === "weekly"
      ? requestedView
      : "today";
    return (
      <CustomerCrmWorkbench
        initialCustomerId={route.params.get("customerId") ?? undefined}
        initialOwnerUserId={route.params.get("ownerUserId") ?? ""}
        initialWeekStart={route.params.get("weekStart") ?? ""}
        initialView={initialView}
        user={effectiveUser}
      />
    );
  }

  if (route.path === "/mobile/work/customers/review") {
    return (
      <CustomerReviewWorkbench
        customerId={route.params.get("customerId") ?? ""}
        user={effectiveUser}
      />
    );
  }

  if (isMobileWorkPath(route.path)) {
    return (
      <>
        {toast ? <div className={`toast ${toast.tone}`}>{toast.message}</div> : null}
        <MobileWorkPage onLogout={mobileWorkPreviewUser ? () => navigate("/login") : logout} route={route} user={effectiveUser} />
      </>
    );
  }

  if (!canMaintainPayroll(effectiveUser)) {
    return (
      <AppShell onLogout={logout} route={route} user={effectiveUser}>
        <NoPermission user={effectiveUser} />
      </AppShell>
    );
  }

  return (
    <AppShell onLogout={logout} route={route} user={effectiveUser}>
      {toast ? <div className={`toast ${toast.tone}`}>{toast.message}</div> : null}
      {route.path === "/finance/workbench" || route.path === "/finance/workbench-gray" ? (
        <FinanceWorkbenchGrayPage route={route} setToast={setToast} />
      ) : (
        <PayrollBatchPage route={route} setToast={setToast} user={effectiveUser} />
      )}
    </AppShell>
  );
}
